import * as d3 from 'd3'
import { EventEmitter } from 'events'
import debounce from 'lodash.debounce'
import type { Map as MapLibreMap } from 'maplibre-gl'
import Renderer from './renderer'
import Adapter from './adapter'
import { hoverOrphaned } from './update'
import {
  boundsOf,
  byName,
  clearOf,
  clusterCount,
  findRoom,
  groupScale,
  openZooms,
  ringFit,
  ringOffsets,
  labelSpots,
  overlapArea,
  rectAt,
  sideOf,
  layoutView,
  networkGoal,
  openFitZoom,
  openZoomOf,
  placesKey,
  resolveLinks,
  planGraph,
  planPlaces,
  solidLink,
  viewGraph,
  withoutNodeName,
  type Box,
  type Cluster,
  type GraphPlan,
  type LabelSpot,
  type PlannedFloat,
  type PlannedHub,
  type Rect,
  type Place,
  type Spot,
} from './mapPlaces'
import { TRANSITION_DURATION } from '../constants'
import { CLOUD_PLACE_SIZE } from './constants'
import { drawCloudPlace, placeMarks } from './marks'
import { mergeArcs } from './networkHues'
import { NODE_MARK_TITLES, cloudPlaceId, marksTitle } from '../utilities/nodeMarks'

/**
 * The map view (nobgp-router#1934): the SAME graph as `core.ts` draws, on a map.
 *
 * What is the same: the adapter (so the rail switches, a selection, a search and a hover mean
 * what they mean in the graph), the renderer (so every glyph is the one the graph draws), and
 * the events this class emits (so `Graph.tsx` drives both engines with one set of handlers).
 *
 * What differs is where things are, and `mapPlaces.ts` decides it: a location node is pinned at
 * its real place, the home icon at the viewer's place, and each node on a SEAT of a round ring
 * round its location, in name order clockwise from 12 o'clock. A ring is as wide as its badges
 * need, and a seat where two badges meet moves out a little (`fitRings`, `ringFit`). A service is
 * placed where there is room round its node (`placeLeaves`). The simulation moves only
 * networks, each to the middle of what it links to; it runs off screen, and what moves glides
 * (`settle`).
 *
 * ⚠ Nodes were free for a while (Benoit, 2026-10-03: "should be able to move to adapt"), and the
 * rings came out bent ("too distorted now, keep them round"). They are pinned again.
 *
 * ⚠⚠ NOTHING THAT IS DRAWN LIES ON ANOTHER (Benoit, 2026-10-03: "let's enforce that nodes
 * (location, nodes, services, networks) and labels (chips, halo labels, etc..) cannot
 * overlap"). Each drawn thing is ONE box, its glyph and its label (`measure`), and `clearBoxes`
 * moves the boxes apart. What is pinned does not move: what is near it moves away. Two pinned
 * things are the layout's to keep apart (`ringFit` within a ring, `layoutView` between rings,
 * which spaces them by how far their badges reach).
 *
 * ⚠ A location node has NO LABEL here (Benoit, 2026-10-01). Its label in the graph is the name
 * of the city, and the map under it says that.
 *
 * ⚠⚠ A SEPARATE ENGINE, ON PURPOSE. `core.ts` and `force.ts` hold a layout that was tuned by
 * measurement, ring by ring, and almost every line of it is about wedges and radii that a map
 * does not have. A mode switch through that code would put every one of those rules at risk for
 * a view that uses none of them. The cost is the join code below, which repeats `core.ts`.
 * ⚠ The two engines are given the SAME `<svg>`, one after the other. So each takes its own
 * listeners off it in `destroy()`: see the note there in `core.ts`, which is the one change this
 * view made to that file.
 *
 * ⚠ The map takes the pan and the zoom, not d3. The `<svg>` lies over the map and takes no
 * pointer events of its own (`styles.ts`), so a drag on an empty part reaches the map. On every
 * move of the map the pinned nodes and the seats are projected again.
 */

type UpdateFn = (
  data: MergedData,
  activeId?: string,
  selectedId?: string,
  query?: string
) => void

interface MapParams {
  svg: SVGSVGElement
  /** The element the map draws into, under the `<svg>`. */
  mapElement: HTMLElement
  width: number
  height: number
  dark: boolean
}

// OpenFreeMap: free tiles from OpenStreetMap data, with no key (Benoit, 2026-10-01).
const STYLE = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
}

// ⚠ The GeoLite license requires this credit where the data is shown, and every place on this
// map is GeoLite data. The map's own credit (OpenFreeMap, OpenStreetMap) comes from its style.
const MAXMIND_CREDIT = 'Location data by <a href="https://www.maxmind.com" target="_blank" rel="noreferrer">MaxMind</a>'

/**
 * How far in the reader can zoom, and how far a fit goes.
 *
 * ⚠ A place is the centre of a region tens of kilometres wide. Street level would show a node on
 * a building it is not in, so the map stops at about the scale of a city.
 */
const MAX_ZOOM = 11
const FIT_MAX_ZOOM = 8
/** The space kept between what is drawn and the edge of the window. */
const FIT_MARGIN = 40
/** Room for the name of a node on the left or the right side of a ring. */
const LABEL_ROOM = 110
/** The side menu is on the right, over the map. The fit keeps the places clear of it. */
const SIDE_MENU_WIDTH = 190
const SIDE_MENU_NARROW = 64
/** How far a ring node's label is from the middle of the node: its 10 px glyph, a gap, and its chip's padding. */
const LABEL_OFFSET = 21

/** Room for the name of a network, to the right of its icon, when its badge is at the edge of the window. */
const NETWORK_NAME_ROOM = 110
/** How hard a network is pulled to the middle of what it links to. */
const NETWORK_PULL = 0.6
/** The space kept between two boxes (`clearBoxes`): a name's halo is drawn past its box. */
const BOX_GAP = 3
/**
 * How long what moves glides to where it settles (`settle`). Slow enough to follow with the eye
 * (Benoit, 2026-10-03: "it is moving too fast when it reorganizes on hover").
 */
const GLIDE_DURATION = 700
/** The most ticks `settle` runs before it draws. The simulation is still long before that. */
const SETTLE_TICKS = 300
/**
 * How long a node's services stay after the pointer leaves the node, so that the reader can reach
 * one (Benoit, 2026-10-03: "it should stay put long enough so I can move the mouse to the service
 * and click on the service").
 */
const LEAVE_DELAY = 1000
/** The distances the last resort of `placeLeaves` tries, when no column fits: each one on its own. */
const LEAF_RADII = [18, 30, 46, 66, 90, 120]
/**
 * The least space between a node's badge and its column, and at the column's sides from every
 * other badge (Benoit, 2026-10-03: "there should be a minimum spacing between columns, this is too
 * close"; then lowered from 24: "could the minimum spacing be lowered a bit").
 */
const LEAF_MIN_GAP = 18
/**
 * How far the point at the name end (`nameEnd`) is inside the badge: half a node's icon (8.5) and
 * the 3 px a badge reaches past it (`badgeOf`).
 */
const NAME_END_INSET = 11.5
/** The distances `placeLeaves` tries for a node's column, from that point, nearest first. */
const LEAF_FAN = Array.from({ length: 40 }, (_, i) => NAME_END_INSET + LEAF_MIN_GAP + i * 6)
/** How many bearings the last resort of `placeLeaves` tries round a node, to both sides of the one it prefers. */
const LEAF_STEPS = 24
/** The most a group grows as the map is zoomed in (`groupScale`; Benoit: "no more than twice"). */
const MAX_SCALE = 2
/** The space between two services in a node's column (`placeLeaves`). */
const LEAF_ROW_GAP = 4
/** How near straight up or down (the sine of the bearing) a column is centred above or below. */
const LEAF_VERTICAL = 0.8
/** How far from 3 or 9 o'clock (the sine of its seat) a node's column slides only away from the ring. */
const LEAF_ROW_SIDE = 0.3
/**
 * The least gap above and below a node's column from the badges near it: the gap between two rows
 * of a ring. At 10 a single service did not fit on its node's row beside the next node's badge, and
 * went out past it (Benoit, 2026-10-03: "there is room to have the service at the same level").
 */
const LEAF_END_GAP = 4
/** What a column beside the dot costs, in pixels further out beside the name end. */
const DOT_SIDE_COST = 160
/** The space between a service's icon and its name, in its badge (`badgeOf`). */
const BADGE_GAP = 4
/**
 * The box of a service's icon, open (`renderer.ts`: 6 × 3 across, and its outline). ⚠ Not the
 * group's `getBBox`: the group holds more than the icon, and the badge came out twice as tall.
 */
const SERVICE_ICON: Box = { x: -10.5, y: -10.5, width: 21, height: 21 }

/**
 * Every node, service and network on the map is a BADGE: one box that holds its icon and its name,
 * the icon at its left end (Benoit, 2026-10-03: "...nodes as well with round corners so we do not
 * have to worry about separate label nodes. maybe even networks"). A node's and a network's badge
 * is a pill; a service's has the square corners of its icon.
 */
const BADGE_TYPES = new Set<GraphNode['type']>(['node', 'network', 'service'])
/** The box of each badge's icon, from the middle of its node. Not `getBBox`: a group holds more. */
/**
 * A node's dot on the map, as a part of its size on the graph (Benoit, 2026-10-03: "the node badges
 * are too tall, make them smaller"). 10 px across the graph's 14, so the badge is 20 px tall.
 */
const NODE_SCALE = 0.7
const BADGE_ICONS: Partial<Record<GraphNode['type'], Box>> = {
  node: { x: -8.5, y: -8.5, width: 17, height: 17 },
  network: { x: -7.5, y: -7.5, width: 15, height: 15 },
  service: SERVICE_ICON,
}
/** The ring of a closed data center icon in the highlight by network: outside its count. */
const CLOSED_ARC_RADIUS = 24
/** The network icon on the map: one box linked to two, in place of the graph's shield. 14 px across. */
const NETWORK_ICON_LINES = 'M0,-2.4 V0 M-4,2.4 V0 H4 V2.4'
const COLLIDE: Partial<Record<GraphNode['type'], number>> = {
  // The home icon (`drawHome`): the size of a location's.
  account: 20,
  network: 26,
  // The data center icon. What hangs off a node must not settle on it: a click on it toggles its ring.
  location: 20,
  node: 15,
  service: 22,
}


/** What a cloud location is near, with where each one is: its kept place holds while this is the same. */
const floatNear = (plan: GraphPlan, float: PlannedFloat) =>
  float.near
    .map(id => {
      const place = plan.account?.id === id ? plan.account.place : plan.hubs.find(hub => hub.id === id)?.place
      return place ? `${id}@${place.lat},${place.lng}` : id
    })
    .join(' ')

/** Who may move what on the map (nobgp-router#1976). `Graph.tsx` knows the reader's roles. */
export interface MoveRules {
  /** Whether the reader controls every one of these nodes (model ids): its owner, or an org Owner or Admin. */
  nodes: (nodeIds: string[]) => boolean
  /** Whether the reader may place the cloud locations: an Owner or Admin of the active org. */
  clouds: boolean
}

/** What a drag moves. See `moveTarget`. */
type MoveTarget =
  | { kind: 'nodes'; id: string; nodeIds: string[]; label: string; cloud?: undefined }
  | { kind: 'cloud'; id: string; cloud: CloudPlace; nodeIds: string[]; label: string }

/** What the layout places (`Spot`): a location and the user icon are pinned there, a node has its seat there. */
const isPlacedType = (node: GraphNode) => node.type === 'location' || node.type === 'node' || node.type === 'account'

export default class MapNetwork extends EventEmitter {
  private map?: MapLibreMap
  private destroyed = false
  private fitted = false
  /** `fit` is moving the map (`moveend`). */
  private fitting = false
  private userMoved = false
  private pending?: Parameters<UpdateFn>
  private last?: Parameters<UpdateFn>
  private viewer: Place | null = null
  private self?: string
  /** The org whose nodes the data holds. See `setOrg`. */
  private org?: string | null
  private unplaced = 0
  private plan?: GraphPlan
  /** `placesKey` of the plan the map was last fitted for. See `draw`. */
  private places = ''
  /** The data center icons the map draws now, by id. `reproject` works them out on every move. */
  private clusters = new Map<string, Cluster>()
  /** The locations that the reader closed with a click. See `toggle`. */
  private folded = new Set<string>()
  /** Who may move what (nobgp-router#1976), from `Graph.tsx`. See `moveTarget`. */
  private moveRules: MoveRules = { nodes: () => false, clouds: false }
  /** The point of each cloud location that an Owner or Admin placed (`org_cloud_places`), by its id. */
  private cloudPoints = new Map<string, Place>()
  /**
   * Where each cloud location was drawn last, as a place on the earth, by its id, with the places
   * of what it is near then (`floatNear`). It stays there while it is in the window (`placeFloats`).
   * A fit forgets it, and so does a change of what it is near.
   */
  private floatPlaces = new Map<string, { place: Place; near: string }>()
  /** The model ids of the nodes whose place a person set (`node_location_overrides`). */
  private setNodes: ReadonlySet<string> = new Set()
  /**
   * Where the reader dragged a location, the home icon or a cloud location, by its id. The layout
   * draws it there until `Graph.tsx` has stored the place, or the reader cancelled (`release`).
   */
  private moving = new Map<string, Place>()
  /** The next frame of a drag, so that a fast pointer redraws once a frame. */
  private dragFrame?: number
  /** The groups that the user icon stands for, by the id of that icon. It carries their count. */
  private riding = new Map<string, Cluster>()
  /** The ids of those icons as one string, and whether the joins must run whatever it says. */
  private clusterKey = ''
  private rejoin = false
  /** The icons' nodes and links, the same objects on every draw. See `viewGraph`. */
  private held = { nodes: new Map<string, GraphNode>(), links: new Map<string, GraphLink>() }
  /** Where each pinned node and each seat is on the screen now. `reproject` works it out on every move. */
  private spots = new Map<string, Spot>()
  /** The links from a node to the user icon that stands in for its location. See `planGraph`. */
  private merged = new Map<string, GraphLink>()
  private nodes: GraphNode[] = []
  private links: GraphLink[] = []
  /** For each free node that the layout does not place, the placed nodes it hangs off. See `reproject`. */
  private anchors = new Map<string, GraphNode[]>()
  /** Where each network goes: the middle of what it links to, or the edge of the window (`networkGoal`). */
  private goals = new Map<string, { x: number; y: number }>()
  /** The nodes that had no position before this draw. `reproject` puts each at what it hangs off. */
  private fresh = new Set<string>()
  /** The box of each drawn thing, its glyph and its label, from its middle. Set by `measure`. */
  private boxes = new Map<string, Box>()
  /** The box of each drawn thing's glyph alone, from its middle. Set by `measure`. */
  private glyphs = new Map<string, Box>()
  /** Where each label is, round its glyph (`placeNames`). Kept while it is free, so names do not hop. */
  private nameSpots = new Map<string, LabelSpot>()
  /** Each ring's least radius, its badges' reach and its seats' offsets, so that badges fit (`fitRings`). */
  private fits = { min: new Map<string, number>(), out: new Map<string, number>(), reach: new Map<string, number>() }
  /** The nodes each ring was fitted for, in seat order: a ring whose nodes changed is fitted again. */
  private ringMembers = new Map<string, string>()
  /** The lowest zoom at which each group opens (`openZooms`), for the plan and fits it was worked out on. */
  private opens = new Map<string, number>()
  private opensKey = ''
  /** Counts the changes to `fits`, so that `opens` is worked out again after one. */
  private fitsVersion = 0
  /** The zoom of the last fit: a group is first seen at its size there, or where it opens (`groupScale`). */
  private baseZoom = 0
  /** Things that close because of a move of the map go at once, with no fade (`join`). */
  private exitDuration = TRANSITION_DURATION
  /**
   * The services that have been drawn where they settled. Each STAYS there while it is
   * drawn: it is fixed, and what comes later moves round it (`settle`).
   */
  private still = new Map<string, string>()
  /** What was drawn at the last `settle`. Something new appears where it settles, with no glide. */
  private settled = new Set<string>()
  /** The glide in progress (`settle`): where each thing goes. `reproject` ends it first. */
  private glide?: { timer: d3.Timer; to: Map<string, { x: number; y: number }> }
  private leaveTimeout?: NodeJS.Timeout
  private width: number
  private height: number
  private dark: boolean

  private readonly svg: d3.Selection<SVGSVGElement, unknown, null, undefined>
  private readonly simulation: d3.Simulation<GraphNode, GraphLink>
  private readonly renderer: Renderer
  private readonly chips: d3.Selection<SVGGElement, unknown, null, undefined>
  /** A hole for each badge in the mask over the lines (`placeHoles`). */
  private readonly holes: d3.Selection<SVGGElement, unknown, null, undefined>
  // ⚠ One location for each place: a ring does not say a network on the map (app#498).
  private readonly adapter = new Adapter({ onePerPlace: true })

  constructor({ svg, mapElement, width, height, dark }: MapParams) {
    super()
    this.width = width
    this.height = height
    this.dark = dark
    this.svg = d3.select(svg)
    // ⚠ `zoom` is the class `Graph.tsx` looks for to know that the drawing is there. `map` is
    // what the map rules in `styles.ts` hang on.
    this.renderer = new Renderer(this.svg.append('g').attr('class', 'zoom map'))
    // The chips behind the names of nodes and networks (`placeChips`). ⚠ UNDER every glyph, and
    // the names over everything: a service, and its own name, is drawn over a node's
    // chip (Benoit, 2026-10-02). A chip starts past its node's glyph, so no glyph covers a chip.
    const points = this.renderer.nodeContainer.node()!
    this.chips = d3.select(points.parentNode as SVGGElement).insert('g', () => points).attr('class', 'chips')
    // ⚠ The lines are MASKED where a badge is (Benoit, 2026-10-03: "clip the lines such that we do
    // not see them through the transparency of the badges"): a badge keeps its transparency over
    // the map, and a line stops at its edge. One hole for each badge (`placeHoles`).
    const maskId = `map-lines-${Math.random().toString(36).slice(2)}`
    const mask = d3
      .select(points.parentNode as SVGGElement)
      .append('defs')
      .append('mask')
      .attr('id', maskId)
      .attr('maskUnits', 'userSpaceOnUse')
      .attr('x', -1e5)
      .attr('y', -1e5)
      .attr('width', 2e5)
      .attr('height', 2e5)
    mask.append('rect').attr('x', -1e5).attr('y', -1e5).attr('width', 2e5).attr('height', 2e5).attr('fill', 'white')
    this.holes = mask.append('g')
    this.renderer.linkContainer.attr('mask', `url(#${maskId})`)

    this.simulation = d3
      .forceSimulation<GraphNode, GraphLink>()
      // ⚠ A node pushes nothing away. A ring is nodes thirty pixels apart, and their charge pushed
      // the ring open against its seats. The collide force and `clearBoxes` keep them apart.
      .force('charge', d3.forceManyBody<GraphNode>().strength(d => (d.type === 'node' || d.type === 'network' ? 0 : -90)).distanceMax(220))
      // A network is pulled to the middle of what it links to (`reproject`). ⚠ The only thing the
      // simulation moves: a node is pinned to its seat, and a service is placed where
      // there is room (`placeLeaves`).
      .force('outX', d3.forceX<GraphNode>(d => this.goals.get(d.id)?.x ?? d.x ?? 0).strength(d => this.pullOf(d)))
      .force('outY', d3.forceY<GraphNode>(d => this.goals.get(d.id)?.y ?? d.y ?? 0).strength(d => this.pullOf(d)))
      .force(
        'collide',
        d3.forceCollide<GraphNode>().radius(d => (d.cloudPlace ? CLOUD_PLACE_SIZE / 2 + 6 : (COLLIDE[d.type] ?? 16)))
      )
      .force('clear', () => this.clearBoxes())
      .stop()

    // A wheel over a node goes to the node and not to the map under it. Hand it on, so the zoom
    // does not stop working wherever the cursor happens to be on a glyph.
    svg.addEventListener('wheel', this.forwardWheel, { passive: false })

    void this.open(mapElement)
  }

  /**
   * Load the map library and open the map.
   *
   * ⚠ `import()`, so only a reader who opens the map downloads the library. A draw that arrives
   * before the map is ready is kept and drawn when it is.
   *
   * ⚠⚠ A MAP THAT CANNOT OPEN IS `unavailable` AND NOT `error`. `error` makes `Graph.tsx` build
   * the engine again, and a browser with no WebGL fails the same way every time. The second
   * argument of the event says if the browser is the cause (below).
   */
  private async open(mapElement: HTMLElement) {
    // ⚠⚠ TWO WAYS TO FAIL, AND ONLY ONE IS THE BROWSER'S. The library is a chunk of its own, with
    // the hash of its build in its name. A tab that was loaded before a deploy asks for a file
    // that is gone, and in this repo each merge is a deploy. That is `unavailable` with `false`:
    // go back to the graph and KEEP the reader's choice, because a reload opens the map. A browser
    // that cannot draw the map (no WebGL) is `unavailable` with `true`, and the choice is stored
    // off, so the next load does not download a megabyte to fail again.
    let library: typeof import('./maplibre')
    try {
      library = await import('./maplibre')
    } catch (error) {
      if (this.destroyed) return
      if (this.listenerCount('unavailable')) this.emit('unavailable', error, false)
      else console.error('the map library did not load', error)
      return
    }
    try {
      const { default: maplibregl } = library
      if (this.destroyed) return
      const map = new maplibregl.Map({
        container: mapElement,
        // ⚠ Read NOW and not when the engine was built: the theme can settle while the library
        // loads, and `setDark` has no map to restyle until this line has run.
        style: this.dark ? STYLE.dark : STYLE.light,
        center: [0, 25],
        zoom: 1.4,
        maxZoom: MAX_ZOOM,
        // North is up and the view is flat: the overlay is projected with x and y only.
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        renderWorldCopies: false,
        doubleClickZoom: false,
        attributionControl: false,
      })
      map.touchZoomRotate.disableRotation()
      map.keyboard.disableRotation()
      // ⚠⚠ THE CREDITS ARE ALWAYS SHOWN (Benoit, 2026-10-02, nobgp/app#449). `compact: true` put
      // them behind the ⓘ button after the first drag, and the MaxMind credit is a license
      // requirement. `compact: false` keeps them at every width. They are small and quiet instead
      // (`styles.ts`), and they wrap on a phone.
      map.addControl(new maplibregl.AttributionControl({ compact: false, customAttribution: MAXMIND_CREDIT }), 'bottom-right')
      // The + and − buttons (Benoit, 2026-10-01). No compass: the map does not rotate.
      // ⚠ Bottom RIGHT, above the credits. They were bottom left, where the open drawer is over
      // them (nobgp/app#449): a reader who selected a node could not zoom with the buttons.
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')
      map.on('movestart', event => {
        // `originalEvent` is set for a move the reader made, and absent for our own fit.
        if ((event as { originalEvent?: unknown }).originalEvent) this.userMoved = true
      })
      map.on('move', () => this.reproject())
      // During a move everything is carried with the map. At its end, what the new zoom brought
      // together is moved apart (`settle`).
      // ⚠ Not inside `fit`: its moves have no duration, so each one ends at once, and the fit
      // settled up to nine times on the nodes of the draw before.
      map.on('moveend', () => {
        if (!this.fitting) this.settle(0.3)
      })
      map.on('click', () => this.emit('close'))
      map.on('dblclick', () => {
        this.fit()
        this.settle(0.3)
      })
      // A tile that fails to load is not a failed draw. But the reader is told: with no tiles the
      // nodes are on an empty page, and nothing else says why (nobgp/app#449).
      map.on('error', event => {
        console.warn('map:', event.error?.message ?? event)
        this.setTrouble(true)
      })
      // A tile that arrives ends it, and so does a map at rest with every tile in view loaded: an
      // error after the last tile (a sprite, one tile) left the note up over a full map.
      map.on('data', event => {
        if ((event as { tile?: unknown }).tile) this.setTrouble(false)
      })
      map.on('idle', () => {
        if (map.areTilesLoaded()) this.setTrouble(false)
      })
      this.map = map
      if (this.pending) {
        const pending = this.pending
        this.pending = undefined
        this.update(...pending)
      }
    } catch (error) {
      if (this.destroyed) return
      if (this.listenerCount('unavailable')) this.emit('unavailable', error, true)
      else console.error('the map could not be opened', error)
    }
  }

  /**
   * Draw the data, 200 ms debounced with a leading call, as `core.ts` does.
   *
   * ⚠ A failed draw is an `error` event and never a throw, for the reason `core.ts` gives.
   */
  update = debounce<UpdateFn>(
    (...args) => {
      try {
        this.draw(...args)
      } catch (error) {
        if (this.listenerCount('error')) this.emit('error', error)
        else console.error('map draw failed after the map was destroyed', error)
      }
    },
    200,
    { leading: true }
  )

  /** Whether the map's tiles do not load. `Graph.tsx` says so on the page. */
  private trouble = false
  private setTrouble(on: boolean) {
    if (on === this.trouble || this.destroyed) return
    this.trouble = on
    this.emit('trouble', on)
  }

  /** The viewer's own account: the one the map draws as the home icon (`planGraph`). */
  setSelf(self: string | undefined) {
    if (self === this.self) return
    this.self = self
    if (this.last) this.update(...this.last)
  }

  /**
   * The org whose nodes the data holds (`loadedOrgId` of `useNode`).
   *
   * ⚠⚠ A SWITCH OF ORG IS A NEW PICTURE (Benoit, 2026-10-20: after a switch with the org drop-down
   * the map did not fit to the new org, and not all of its nodes were in view). The frame that the
   * reader moved to was on the places of the org before, so it no longer holds. The map is fitted
   * again, and it follows the new org's places as they arrive (its `node_metadata` rows come after
   * its nodes), until the reader moves it again. New data of the SAME org does not come here, so a
   * frame that the reader moved stays.
   */
  setOrg(org: string | null | undefined) {
    if (org === this.org) return
    this.org = org
    this.userMoved = false
    this.fitted = false
  }

  /** Where the viewer is, from the router. Draws again when it changes. */
  setViewer(viewer: Place | null) {
    if (viewer?.lat === this.viewer?.lat && viewer?.lng === this.viewer?.lng) return
    this.viewer = viewer
    // The home icon and the locations with no place of their own have just come or gone, so the
    // frame the reader has not touched is fitted again.
    if (!this.userMoved) this.fitted = false
    if (this.last) this.update(...this.last)
  }

  setDark(dark: boolean) {
    if (dark === this.dark) return
    this.dark = dark
    this.map?.setStyle(dark ? STYLE.dark : STYLE.light)
  }

  updateSize(width: number, height: number) {
    this.width = width
    this.height = height
    if (!this.map) return
    this.map.resize()
    if (!this.userMoved) {
      this.fit()
      this.settle(0.3)
    } else this.reproject()
  }

  destroy() {
    this.destroyed = true
    this.update.cancel()
    clearTimeout(this.leaveTimeout)
    this.glide?.timer.stop()
    if (this.dragFrame !== undefined) cancelAnimationFrame(this.dragFrame)
    this.simulation.stop()
    this.removeAllListeners()
    this.svg.node()?.removeEventListener('wheel', this.forwardWheel)
    this.svg.selectAll('*').remove()
    this.map?.remove()
    this.map = undefined
  }

  private forwardWheel = (event: WheelEvent) => {
    const canvas = this.map?.getCanvas()
    if (!canvas || event.target === this.svg.node()) return
    event.preventDefault()
    // ⚠ Field by field. `new WheelEvent(type, event)` does not copy the pointer's position, and
    // the map then zooms about its top left corner and not about the cursor.
    canvas.dispatchEvent(
      new WheelEvent('wheel', {
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
        clientX: event.clientX,
        clientY: event.clientY,
        screenX: event.screenX,
        screenY: event.screenY,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        bubbles: true,
        cancelable: true,
      })
    )
  }

  private draw: UpdateFn = (data, activeId, selectedId, query) => {
    const args: Parameters<UpdateFn> = [data, activeId, selectedId, query]
    this.last = args
    if (!this.map) {
      this.pending = args
      return
    }

    const rerender = this.adapter.transform(data, activeId, selectedId, query)
    const metadata = new Map(data.nodeMetadata.map(row => [row.nodeId, row]))
    const plan = planGraph(
      Array.from(this.adapter.nodeMap.values()),
      Array.from(this.adapter.linkMap.values()),
      metadata,
      this.viewer,
      this.merged,
      this.self
    )
    this.plan = plan
    if (plan.unplaced !== this.unplaced) {
      this.unplaced = plan.unplaced
      this.emit('unplaced', plan.unplaced)
    }
    // ⚠⚠ THE FRAME FOLLOWS THE PLACES UNTIL THE READER MOVES THE MAP. The engine is not built
    // again when the data changes, so one fit on the first draw left the map where it was: after
    // a switch to an org on another continent, the map showed the places of the org before, with
    // nothing on them. A reader who moved the map keeps their frame, and a double click fits.
    const places = placesKey(plan)
    if (places !== this.places && !this.userMoved) this.fitted = false
    this.places = places
    if (!this.fitted) this.fit()

    // The data changed, so the joins run whatever the map's zoom says. `reproject` does the rest:
    // what is drawn depends on the zoom (a busy group is one data center icon), so the same code
    // draws after new data and after a move of the map.
    this.rejoin = true
    this.reproject()

    if (hoverOrphaned(this.nodes, activeId)) this.emit('leave')

    // ⚠ A new draw moves no node that is drawn already: a hover, a selection or a realtime update
    // shows things, and only those look for room (`settle`). ⚠ `rerender` cannot tell: a hover
    // that shows a node's services is a rerender too.
    this.settle(rerender ? 0.9 : 0.25, false)
  }

  /**
   * Work out where everything settles BEFORE drawing it there, then glide to it.
   *
   * ⚠⚠ THE SIMULATION NEVER RUNS ON SCREEN. It ran live, and on a hover the services that came
   * pushed the nodes round as fast as the forces went, and the services moved under the pointer
   * that went to click them (Benoit, 2026-10-03). Now it runs to its end here, off screen
   * (`simulation.tick` draws nothing), and then:
   * - what was drawn before glides to where it settled, over `GLIDE_DURATION`;
   * - what is new appears where it settled, and fades in: it does not fly in;
   * - a service that was drawn before is fixed where it is (`still`), so it does not move
   *   while the reader goes to it. What comes later moves round it. ⚠ Except its own column: when
   *   one joins it or leaves it, the whole column is placed again.
   *
   * ⚠⚠ ON A HOVER OR A SELECTION NO NODE MOVES (`moveNodes` false). It did, and the node under the
   * pointer moved out from under it: the hover ended, and its services went (Benoit, 2026-10-03:
   * "the nodes still move a little fast making it hard to click on the node or service I want").
   * Only what the hover shows moves, to where there is room. The nodes give way when the zoom
   * changes, or a location opens or closes; a node that is new finds its room at once.
   */
  private settle(alpha: number, moveNodes = true) {
    this.endGlide()
    const drawn = new Set(this.nodes.map(node => node.id))
    // ⚠ A service stays only while it hangs off the node it was placed round (`leafKey`).
    const byId = new Map(this.nodes.map(node => [node.id, node]))
    const leaf = (node: GraphNode) => node.type === 'service'
    // ⚠ A NEW service JOINS its node's column: the whole column is placed again, in name
    // order. With the others fixed, the new one was a column of one, centred on the node's row,
    // where the others were in its way: it moved out past them and was drawn beside the stack, on
    // the same row (Benoit, 2026-10-03: "when I registered a second service it showed up on the
    // left instead of under the existing one in a stack"). The same when one LEAVES a column: the
    // others stayed, with its place empty between them.
    // ⚠ Only one that was not drawn before, or that is gone.
    const changed = new Set(this.nodes.filter(node => leaf(node) && !this.settled.has(node.id)).map(node => this.leafKey(node)))
    this.still.forEach((key, id) => {
      const node = byId.get(id)
      if (!node) changed.add(key)
      if (!node || this.leafKey(node) !== key) this.still.delete(id)
    })
    changed.delete('')
    this.still.forEach((key, id) => {
      if (!changed.has(key)) return
      this.still.delete(id)
      const node = byId.get(id)!
      node.fx = null
      node.fy = null
    })
    this.nodes.forEach(node => {
      if (!leaf(node) || !this.still.has(node.id) || node.x === undefined || node.y === undefined) return
      node.fx = node.x
      node.fy = node.y
    })
    const from = new Map(this.nodes.map(node => [node.id, { x: node.x ?? 0, y: node.y ?? 0 }]))
    // Held where they are for this settle only, when they must not move.
    const held = moveNodes
      ? []
      : this.nodes.filter(node => !leaf(node) && node.fx == null && node.x !== undefined && node.y !== undefined && this.settled.has(node.id))
    // ⚠ The sides of the names first, then the simulation: a badge's box depends on its side, and a
    // badge on the left of a ring measured as if it were on the right lay on its neighbour.
    this.placeNames()
    // Each ring as wide as its badges need. A ring that changed is laid out again first.
    if (this.fitRings(moveNodes)) this.reproject()
    held.forEach(node => {
      node.fx = node.x
      node.fy = node.y
    })

    // ⚠⚠ d3's forceX and forceY read each node's target ONCE, when they are given the nodes, and
    // keep it. A move of the map moves every seat and every goal, and the forces pulled each node
    // back to where its seat WAS on the screen: after a zoom the rings stayed where they had been
    // and their locations went (Benoit, 2026-10-03, "when zooming out fast"). Read them again.
    ;(['outX', 'outY'] as const).forEach(name => {
      const force = this.simulation.force(name) as d3.ForceX<GraphNode> & d3.ForceY<GraphNode>
      if (name === 'outX') force.x(force.x())
      else force.y(force.y())
    })
    this.simulation.stop().alpha(alpha).alphaDecay(0.05)
    for (let i = 0; i < SETTLE_TICKS && this.simulation.alpha() > this.simulation.alphaMin(); i++) this.simulation.tick()
    held.forEach(node => {
      node.fx = null
      node.fy = null
    })
    // What is new is placed where there is room, round its node. Then every name, round its glyph.
    this.placeLeaves(this.nodes.filter(node => leaf(node) && !this.still.has(node.id)))
    this.placeNames()

    const to = new Map<string, { x: number; y: number }>()
    this.nodes.forEach(node => {
      node.vx = 0
      node.vy = 0
      to.set(node.id, { x: node.x ?? 0, y: node.y ?? 0 })
      // What is new starts where it settled.
      if (!this.settled.has(node.id)) from.set(node.id, to.get(node.id)!)
      if (leaf(node)) this.still.set(node.id, this.leafKey(node))
    })
    this.settled = drawn

    const place = (t: number) => {
      this.nodes.forEach(node => {
        const a = from.get(node.id)
        const b = to.get(node.id)
        if (!a || !b) return
        node.x = a.x + (b.x - a.x) * t
        node.y = a.y + (b.y - a.y) * t
        if (node.fx != null) {
          node.fx = node.x
          node.fy = node.y
        }
      })
      this.tick()
    }
    place(0)
    const timer = d3.timer(elapsed => {
      const t = Math.min(1, elapsed / GLIDE_DURATION)
      place(d3.easeCubicInOut(t))
      if (t === 1) this.endGlide()
    })
    this.glide = { timer, to }
  }

  /** End the glide in progress: everything goes where it was going. */
  private endGlide() {
    const glide = this.glide
    if (!glide) return
    this.glide = undefined
    glide.timer.stop()
    this.nodes.forEach(node => {
      const b = glide.to.get(node.id)
      if (!b) return
      node.x = b.x
      node.y = b.y
      if (node.fx != null) {
        node.fx = b.x
        node.fy = b.y
      }
    })
    this.tick()
  }

  /**
   * Where the plan's pinned nodes are on the screen, and which groups are data center icons, for
   * the map as it is shown now.
   */
  private layout(): { spots: Map<string, Spot>; clusters: Cluster[] } {
    const map = this.map
    if (!map || !this.plan) return { spots: new Map(), clusters: [] }
    // The window clear of the side menu: a cloud location stays in it (`placeFloats`).
    const menu = this.width > 3 * SIDE_MENU_WIDTH ? SIDE_MENU_WIDTH : SIDE_MENU_NARROW
    const frame = { left: 0, right: this.width - menu, top: 0, bottom: this.height }
    // A cloud is at its point: the one an Owner or Admin placed, or the one the reader drags it to.
    const pinned = new Map(this.cloudPoints)
    this.plan.floats.forEach(float => {
      const moved = this.moving.get(float.id)
      if (moved) pinned.set(float.id, moved)
    })
    const plan = this.movedPlan(this.plan)
    // ⚠ A kept place holds only while what the cloud is near is where it was: a location that moved
    // (a place set by hand) or a network that gained a location gives it another middle.
    const kept = new Map<string, Place>()
    plan.floats.forEach(float => {
      const was = this.floatPlaces.get(float.id)
      if (was && was.near === floatNear(plan, float)) kept.set(float.id, was.place)
    })
    return layoutView(plan, place => map.project([place.lng, place.lat]), map.getZoom(), MAX_ZOOM, this.folded, this.fits, this.groupScales(), frame, pinned, kept)
  }

  /**
   * Keep where each cloud location is drawn now, as a place on the earth (`floatPlaces`).
   *
   * ⚠ Not a cloud with a point (`cloudPoints`, `moving`): it is drawn at its point, and its kept
   * place would hold it there after the point goes (the reader lets the map place it again, or a
   * drag that could not be stored is released).
   */
  private keepFloats(spots: Map<string, Spot>) {
    const map = this.map
    if (!map || !this.plan) return
    const plan = this.movedPlan(this.plan)
    this.floatPlaces = new Map()
    plan.floats.forEach(float => {
      if (this.cloudPoints.has(float.id) || this.moving.has(float.id)) return
      const spot = spots.get(float.id) ?? spots.get(`cluster:${float.id}`)
      if (!spot) return
      const { lng, lat } = map.unproject([spot.x, spot.y])
      this.floatPlaces.set(float.id, { place: { lat, lng }, near: floatNear(plan, float) })
    })
  }

  /** The plan with each location, and the home icon, that the reader is moving at its new place. */
  private movedPlan(plan: GraphPlan): GraphPlan {
    if (!this.moving.size) return plan
    const account = plan.account && this.moving.get(plan.account.id)
    return {
      ...plan,
      account: account && plan.account ? { ...plan.account, place: account } : plan.account,
      hubs: plan.hubs.map(hub => {
        const moved = this.moving.get(hub.id)
        return moved ? { ...hub, place: moved } : hub
      }),
    }
  }

  /** Who may move the nodes of a location, and the cloud locations (nobgp-router#1976). */
  setMoveRules(rules: MoveRules) {
    this.moveRules = rules
  }

  /**
   * The places a person set: the point of each cloud location, and the nodes whose place was set.
   * Draws again when they change.
   */
  setPlaces(clouds: Partial<Record<CloudPlace, Place>>, setNodes: ReadonlySet<string>) {
    const points = new Map(Object.entries(clouds).map(([cloud, place]) => [cloudPlaceId(cloud as CloudPlace), place as Place]))
    const same =
      points.size === this.cloudPoints.size &&
      Array.from(points).every(([id, place]) => this.cloudPoints.get(id)?.lat === place.lat && this.cloudPoints.get(id)?.lng === place.lng) &&
      setNodes.size === this.setNodes.size &&
      Array.from(setNodes).every(id => this.setNodes.has(id))
    if (same) return
    this.cloudPoints = points
    this.setNodes = setNodes
    if (this.last) this.update(...this.last)
  }

  /**
   * Stop drawing a moved thing at the place the reader dragged it to: its place is stored, and the
   * data draws it there now, or the reader cancelled and it goes back.
   */
  release(id: string) {
    if (!this.moving.delete(id)) return
    if (this.last) this.update(...this.last)
  }

  /** Draw a moved thing at another place: the reader found its place by name (`PlaceDialogs.tsx`). */
  moveTo(id: string, place: Place) {
    if (!this.moving.has(id)) return
    this.moving.set(id, place)
    this.reproject()
  }

  /**
   * What a drag on this glyph moves, or null (nobgp-router#1976):
   * - an open location, or the home icon: the place of each of its nodes, when the reader controls
   *   every one of them. The home icon is the location of the viewer's own place;
   * - a cloud location, open or closed: its point on the map, for an Owner or Admin.
   * ⚠ A data center icon that stands for several locations is not moved: which place would it set?
   *   One that stands for one location is that location.
   */
  private moveTarget(d: GraphNode): MoveTarget | null {
    const plan = this.plan
    if (!plan) return null
    const cluster = this.clusters.get(d.id)
    const cloud = d.cloudPlace ?? cluster?.cloudPlace
    if (cloud) {
      if (!this.moveRules.clouds) return null
      return { kind: 'cloud', id: cluster ? cluster.hubs[0] : d.id, cloud, nodeIds: [], label: NODE_MARK_TITLES[cloud] }
    }
    const modelIds = (ids: string[]) => ids.map(id => this.adapter.nodeMap.get(id)?.modelNodeId).filter((id): id is string => !!id)
    // ⚠ A closed icon moves when it stands for ONE place: one location, or the home icon with only
    // its own nodes. The reader sees the wide view first, and a moved place must be put back from it.
    const riding = this.riding.get(d.id)
    const oneHub = cluster && !cluster.account && cluster.hubs.length === 1 ? cluster.hubs[0] : undefined
    if (cluster && !oneHub) return null
    if (riding && riding.hubs.length) return null
    let id = d.id
    // ⚠ Only the nodes that have a place of their own. A node with none is drawn at the viewer's
    // place, and a place set for it is never drawn (`withSetPlaces` lays a set place over the
    // node's row, and it has no row), so it is not counted as moved.
    const placed = (hub?: PlannedHub) => (hub && !hub.atViewer ? hub.nodes : [])
    let ids: string[] | undefined
    if (oneHub) {
      id = oneHub
      ids = placed(plan.hubs.find(hub => hub.id === oneHub))
    } else if (d.type === 'location') ids = placed(plan.hubs.find(hub => hub.id === d.id))
    else if (d.type === 'account' && plan.account?.id === d.id) ids = plan.account.around.filter(node => !node.atViewer).map(node => node.id)
    const nodeIds = modelIds(ids ?? [])
    if (!nodeIds.length || !this.moveRules.nodes(nodeIds)) return null
    const label = d.type === 'account' ? 'your place' : (this.adapter.nodeMap.get(id)?.label ?? '')
    return { kind: 'nodes', id, nodeIds, label }
  }

  /** Whether every node of this target has a place a person set, so a reset has something to do. */
  private isSet(target: MoveTarget): boolean {
    if (target.kind === 'cloud') return this.cloudPoints.has(target.id)
    return target.nodeIds.every(id => this.setNodes.has(id))
  }

  /** The drag of a location, the home icon or a cloud location (nobgp-router#1976). See `moveTarget`. */
  private dragBehavior() {
    let target: MoveTarget | null = null
    let moved = false
    return d3
      .drag<SVGGElement, GraphNode>()
      .filter((event: MouseEvent, d) => !event.button && !!this.moveTarget(d))
      // ⚠ A press that moves less than this is a click: it opens or closes the location.
      .clickDistance(4)
      .container(() => this.svg.node()!)
      .on('start', (_event, d) => {
        target = this.moveTarget(d)
        moved = false
      })
      .on('drag', event => {
        const map = this.map
        if (!target || !map) return
        const point = map.unproject([event.x, event.y])
        this.moving.set(target.id, { lng: point.lng, lat: point.lat })
        moved = true
        if (this.dragFrame !== undefined) return
        this.dragFrame = requestAnimationFrame(() => {
          this.dragFrame = undefined
          this.reproject()
        })
      })
      .on('end', () => {
        const done = target
        target = null
        if (!done || !moved) return
        const place = this.moving.get(done.id)!
        // ⚠ The place stays where the reader put it until `Graph.tsx` stores it or cancels (`release`).
        if (done.kind === 'cloud') this.emit('moveCloud', { id: done.id, cloud: done.cloud, place })
        else this.emit('moveNodes', { id: done.id, nodeIds: done.nodeIds, label: done.label, place })
      })
  }

  /**
   * How much larger each group is drawn at this zoom (`groupScale`; Benoit, 2026-10-03: "as we zoom
   * further, we should scale the nodes and spread them ... up to a max ... no more than twice the
   * size ... while remaining centered on the location"): from the zoom at which the group is
   * first seen open, which is where it opens or the zoom of the last fit, whichever is later.
   */
  private groupScales(): Map<string, number> {
    const map = this.map
    if (!map || !this.plan) return new Map()
    const zoom = map.getZoom()
    return new Map(Array.from(this.keptOpens(), ([id, open]) => [id, groupScale(zoom, Math.max(open, this.baseZoom), MAX_SCALE)]))
  }

  /** The lowest zoom at which each group opens (`openZooms`), worked out again when the plan or its fits change. */
  private keptOpens(): Map<string, number> {
    const plan = this.plan
    if (!plan) return new Map()
    const key = `${this.places} ${this.fitsVersion} ${plan.hubs.length}`
    if (key !== this.opensKey) {
      this.opens = openZooms(plan, MAX_ZOOM, this.fits)
      this.opensKey = key
    }
    return this.opens
  }

  /** How much larger a drawn thing is: its group's, or for a service, its node's. */
  private scaleOf(d: GraphNode): number {
    const own = this.spots.get(d.id)?.scale
    if (own) return own
    if (d.type !== 'service') return 1
    return this.spots.get(this.leafAnchors(d)[0]?.id ?? '')?.scale ?? 1
  }

  /**
   * Draw what the map shows at this zoom: the plan, with each busy group as one data center icon.
   *
   * ⚠ Called from `reproject` only, when the data changed or when a move of the map opened or
   * closed a group. The d3 joins fade a node in and out, so a group that opens while the reader
   * zooms does not jump.
   */
  private join(clusters: Cluster[], key: string) {
    if (!this.plan) return
    this.clusters = new Map(clusters.filter(cluster => !cluster.account).map(cluster => [cluster.id, cluster]))
    this.riding = new Map(clusters.filter(cluster => cluster.account).map(cluster => [cluster.account!, cluster]))
    this.clusterKey = key
    this.rejoin = false
    const view = viewGraph(this.plan, clusters, this.held)
    // ⚠ The adapter can make a NEW object for a thing that is drawn already. The new object takes
    // the old one's place, or it is placed again as if it were new, and moves under the pointer
    // that goes to it (Benoit, 2026-10-03).
    const old = new Map(this.nodes.map(node => [node.id, node]))
    view.nodes.forEach(node => {
      const was = old.get(node.id)
      if (!was || was === node || node.x !== undefined || was.x === undefined) return
      node.x = was.x
      node.y = was.y
      node.fx = was.fx
      node.fy = was.fy
    })
    this.nodes = view.nodes
    // ⚠⚠ A LINK'S ENDS ARE RESOLVED HERE. The adapter gives each end as an id, and d3's link force
    // used to swap the ids for the nodes; app#455 removed that force, which moved nothing any more,
    // and every line between a node and its services and networks lost its ends and was not
    // drawn (Benoit, 2026-10-03: "there is no line between the node and the service"). A link with
    // an end that is not drawn is not drawn either.
    this.links = resolveLinks(this.nodes, view.links)
    const byId = new Map(this.nodes.map(node => [node.id, node]))

    // What each free node hangs off: a network off its locations and the account, a service or a
    // name off its node. `reproject` moves a free node with them.
    this.anchors = new Map()
    this.links.forEach(link => {
      const source = byId.get(typeof link.source === 'string' ? link.source : link.source.id)
      const target = byId.get(typeof link.target === 'string' ? link.target : link.target.id)
      if (!source || !target) return
      const hang = (free: GraphNode, on: GraphNode) => {
        if (isPlacedType(free) || !isPlacedType(on)) return
        this.anchors.set(free.id, [...(this.anchors.get(free.id) ?? []), on])
      }
      hang(source, target)
      hang(target, source)
    })

    this.updateLinks(this.links)
    this.updateBridges(this.links)
    this.updateNodes(this.nodes)
    this.updateLabels(this.nodes)

    // ⚠ Before `simulation.nodes()`: d3 gives a node with no position one near (0, 0), the top
    // left corner of the window, and after that a new node cannot be told from an old one.
    this.nodes.forEach(node => {
      if (node.x === undefined || node.y === undefined) this.fresh.add(node.id)
    })
    this.simulation.nodes(this.nodes)
  }

  /**
   * A click on a location toggles its nodes (Benoit, 2026-10-02).
   *
   * - A location that is drawn open closes: the icon with its count, where it is.
   * - A location that the reader closed opens again, where it is.
   * - An icon that is closed because its group is too busy at this zoom cannot open where it is:
   *   the click moves the map to its nodes and zooms in (`zoomTo`).
   * - The user icon is the location node of its own place, so it does the same with the nodes
   *   round it ("each location node/datacenter/user should be able to be expanded/collapsed
   *   manually by clicking on it"). ⚠ So on the map a click on the home icon does not select the
   *   account: the drawer's Account tab does. The icon is drawn only with nodes round it.
   */
  private toggle(d: GraphNode): boolean {
    const cluster = this.clusters.get(d.id) ?? this.riding.get(d.id)
    const open = (c: Cluster) => {
      c.hubs.forEach(id => this.folded.delete(id))
      if (c.account) this.folded.delete(c.account)
    }
    if (cluster?.folded) {
      open(cluster)
    } else if (cluster) {
      // The reader wants these nodes: when the map is there, they are drawn.
      open(cluster)
      this.zoomTo(cluster)
      return true
    } else if (d.type === 'location' || d.type === 'account') {
      this.folded.add(d.id)
    } else {
      return false
    }
    this.rejoin = true
    this.reproject()
    this.settle(0.5)
    return true
  }

  /**
   * A click on a data center icon: move the map to its nodes, and zoom to the first zoom at which
   * the map draws them open (Benoit, 2026-10-02). Zoom out again and the icon comes back.
   */
  private zoomTo(cluster: Cluster) {
    const map = this.map
    if (!map || !this.plan) return
    const zoom = openZoomOf(this.plan, this.keptOpens(), cluster, map.getZoom(), MAX_ZOOM, this.fits)
    // The reader asked for this frame: a later draw must not fit the map again.
    this.userMoved = true
    map.easeTo({ center: [cluster.place.lng, cluster.place.lat], zoom: Math.min(MAX_ZOOM, zoom + 0.05), duration: 600 })
  }

  /**
   * Frame the map, clear of the side menu, on EVERY place.
   *
   * ⚠⚠ EVERY LOCATION IS IN THE FIRST FRAME, ALWAYS (Benoit, 2026-10-03: "why is the default zoom
   * not showing all of Jamie's nodes or locations", and of the choices he took "every location in
   * view, always"). The rule of that morning was the most nodes drawn OPEN in 80 % of the frame
   * (`bestFrame`, removed): a fleet with one location far away opened on the others, and nothing
   * but a line that left the window showed the one left out. A fleet on two continents opens as
   * data center icons with counts, and a click on one zooms to its nodes.
   *
   * ⚠ And OPEN when it can be (`openFitZoom`): the fit stopped at `FIT_MAX_ZOOM`, where the rings
   * of a fleet in one metro area touch, so that fleet opened as icons too. The fit now goes in to
   * the first zoom at which every location is open and in the frame.
   *
   * ⚠⚠ THE FIT HAS TWO STEPS, BECAUSE THE RINGS ARE IN PIXELS AND THE PLACES ARE NOT.
   * `fitBounds` frames the places. A ring keeps its size at every zoom, and a group of close
   * locations is spread wider the further out the map is, so what is drawn reaches past the places
   * by an amount that only the layout knows. The second step measures what the layout draws, and
   * zooms out and moves until that is inside the frame.
   */
  private fit() {
    const map = this.map
    if (!map || !this.plan) return
    const bounds = boundsOf(planPlaces(this.plan))
    if (!bounds) return
    // On a phone the menu is a narrow bar of icons (`SideMenuStack.tsx`).
    const menu = this.width > 3 * SIDE_MENU_WIDTH ? SIDE_MENU_WIDTH : SIDE_MENU_NARROW
    // A phone has no room for a name on each side of a ring: the frame keeps the icons in view.
    const room = this.width > 3 * SIDE_MENU_WIDTH ? LABEL_ROOM : 24
    const frame = {
      left: FIT_MARGIN + room,
      right: this.width - menu - FIT_MARGIN - room,
      top: FIT_MARGIN,
      bottom: this.height - FIT_MARGIN,
    }
    // ⚠ A window too small for a frame gets the plain fit. The padding cannot be more than the
    // window holds, or the map refuses it.
    const roomy = frame.right - frame.left > 120 && frame.bottom - frame.top > 120
    const padding = roomy
      ? { top: frame.top, bottom: this.height - frame.bottom, left: frame.left, right: this.width - frame.right }
      : 20
    this.fitting = true
    // A fit places the cloud locations again, from the middle of what they are near.
    this.floatPlaces = new Map()
    // ⚠ Every ring sized first, from its badges or its names (`fitRings`): the frame is measured
    // on the rings as they will be drawn.
    this.fitRings(true)
    const maxZoom = roomy
      ? openFitZoom(this.plan, frame.right - frame.left, frame.bottom - frame.top, FIT_MAX_ZOOM, MAX_ZOOM, this.fits, this.folded)
      : FIT_MAX_ZOOM
    map.fitBounds(bounds, { padding, maxZoom, duration: 0 })

    for (let pass = 0; roomy && pass < 4; pass++) {
      const spots = Array.from(this.layout().spots.values())
      if (!spots.length) break
      const left = Math.min(...spots.map(spot => spot.x))
      const right = Math.max(...spots.map(spot => spot.x))
      const top = Math.min(...spots.map(spot => spot.y))
      const bottom = Math.max(...spots.map(spot => spot.y))
      const over = Math.max((right - left) / (frame.right - frame.left), (bottom - top) / (frame.bottom - frame.top))
      const dx = (left + right) / 2 - (frame.left + frame.right) / 2
      const dy = (top + bottom) / 2 - (frame.top + frame.bottom) / 2
      if (over <= 1 && Math.abs(dx) < 2 && Math.abs(dy) < 2) break
      // Centre what is drawn in the frame, then zoom out by as much as it is too large.
      map.panBy([dx, dy], { duration: 0 })
      if (over > 1) map.setZoom(map.getZoom() - Math.log2(over) - 0.05)
    }
    this.fitting = false
    this.fitted = true
    this.userMoved = false
    // ⚠ Then laid out again: the moves above were drawn with the base zoom of the fit before, so a
    // refit to a nearer zoom drew every group up to twice its size until the reader moved the map.
    this.baseZoom = map.getZoom()
    this.reproject()
  }

  /**
   * Put every pinned node where the map now shows its place, move each node with its seat, and
   * carry the free nodes with them.
   *
   * ⚠ A free node is MOVED BY THE SAME AMOUNT as what it hangs off, before the simulation runs
   * again. Left to the springs alone, a pan would drag every network and every service behind
   * the map like a weight on elastic. A free node that has no position yet starts at the middle
   * of what it hangs off.
   */
  private reproject() {
    const map = this.map
    if (!map) return
    const middle = (nodes: GraphNode[]) => {
      const placed = nodes.filter(node => node.x !== undefined && node.y !== undefined)
      if (!placed.length) return null
      return { x: d3.mean(placed, node => node.x)!, y: d3.mean(placed, node => node.y)! }
    }
    // A move of the map during a glide: everything first goes where it was going.
    this.endGlide()
    // Each group's scale before this move: a service grows away from its node with it.
    const scalesBefore = new Map(Array.from(this.spots, ([id, spot]) => [id, spot.scale ?? 1]))
    // Where each node was. A free node is carried by how far the nodes it hangs off moved.
    const before = new Map<string, { x: number; y: number }>()
    this.nodes.forEach(node => {
      if (node.x !== undefined && node.y !== undefined) before.set(node.id, { x: node.x, y: node.y })
    })

    const view = this.layout()
    this.spots = view.spots
    this.keepFloats(view.spots)
    // ⚠ A move of the map can open or close a group. Then what is DRAWN changes, and not only where.
    const key = view.clusters.map(cluster => cluster.id).join(' ')
    // ⚠ What a zoom closes goes AT ONCE (Benoit, 2026-10-03: "when zooming out the collapsing
    // nodes/labels/etc should hide immediately, not fading out slowly"). A new draw still fades.
    this.exitDuration = this.rejoin ? TRANSITION_DURATION : 0
    if (this.rejoin || key !== this.clusterKey) this.join(view.clusters, key)
    this.nodes.forEach(node => {
      const spot = this.spots.get(node.id)
      if (!spot) return
      // ⚠ A node is PINNED to its seat, as a location is to its place (Benoit, 2026-10-03: "the
      // rings are too distorted now, keep them round"). The ring grows until its badges fit
      // (`fitRings`), and what hangs off a node moves round it.
      node.x = node.fx = spot.x
      node.y = node.fy = spot.y
    })

    this.goals = new Map()
    // The window clear of the side menu, less a margin: a location at the very edge is not "in".
    const menu = this.width > 3 * SIDE_MENU_WIDTH ? SIDE_MENU_WIDTH : SIDE_MENU_NARROW
    const inView = { left: FIT_MARGIN, right: this.width - menu - FIT_MARGIN, top: FIT_MARGIN, bottom: this.height - FIT_MARGIN }
    // Where a badge that is pulled in stops: its icon is at its left end, and its name goes right.
    // ⚠ Never left of `left`: in a window too narrow for the name, the badge went the wrong way.
    const atEdge = { ...inView, right: Math.max(inView.left, inView.right - NETWORK_NAME_ROOM) }
    this.nodes.forEach(node => {
      // A network goes to the MIDDLE of the locations and the user icon it links to, each pulling
      // the same (Benoit, 2026-10-03: "the network location should be more centered between the
      // data centers pulled with equal force from each"). Its links pulled it unevenly before: one
      // per location, with the strength of the location's own links.
      // ⚠ When that middle is out of the window and a location is in it, at the edge of the window
      // on that side (`networkGoal`): the badge is then in the picture, and its lines point to what
      // is not. A middle that is in the window is where the badge stays.
      if (node.type !== 'network') return
      const on = (this.anchors.get(node.id) ?? []).flatMap(a => (a.x !== undefined && a.y !== undefined ? [{ x: a.x, y: a.y }] : []))
      const goal = networkGoal(on, inView, atEdge)
      if (goal) this.goals.set(node.id, goal)
    })
    this.nodes.forEach(node => {
      if (this.spots.has(node.id)) return
      // A node that was pinned by the graph engine's rules, or by an earlier draw, is free here.
      node.fx = null
      node.fy = null
      const on = this.anchors.get(node.id)
      const now = this.goals.get(node.id) ?? (on ? middle(on) : null)
      if (!now) return
      // ⚠ Only the nodes it hung off before, and by how far each MOVED. A name that a second node
      // now reaches too did not move: the middle of its nodes did (Benoit, 2026-10-03: a service
      // must stay put while the reader goes to it).
      const kept = (on ?? []).filter(anchor => before.has(anchor.id) && anchor.x !== undefined && anchor.y !== undefined)
      const was = before.has(node.id) && kept.length ? kept : null
      // A new one starts near what it hangs off; `placeLeaves` then puts a service where
      // there is room. One drawn again starts the same way: where it was is from another draw.
      if (this.fresh.has(node.id) || node.x === undefined || node.y === undefined || !was) {
        // A small offset, so that two free nodes on one anchor do not start on the same pixel.
        const turn = (hash(node.id) % 360) * (Math.PI / 180)
        node.x = now.x + 30 * Math.cos(turn)
        node.y = now.y + 30 * Math.sin(turn)
        return
      }
      node.x += d3.mean(was, anchor => anchor.x! - before.get(anchor.id)!.x)!
      node.y += d3.mean(was, anchor => anchor.y! - before.get(anchor.id)!.y)!
      // ⚠ And away from its node as its group grows (`groupScale`): a zoom in grows the badges.
      const anchor = was[0]
      const ratio = (this.spots.get(anchor.id)?.scale ?? 1) / (scalesBefore.get(anchor.id) ?? 1)
      if (ratio !== 1 && node.type === 'service') {
        node.x = anchor.x! + (node.x - anchor.x!) * ratio
        node.y = anchor.y! + (node.y - anchor.y!) * ratio
      }
      // A service that stays (`still`) is carried with the map, and stays fixed.
      if (this.still.has(node.id)) {
        node.fx = node.x
        node.fy = node.y
      }
    })
    // Used up: each new node has its start now. A pinned node, and a free node with nothing to
    // hang off, were recorded too and have no use for it.
    this.fresh.clear()

    this.tick()
    this.placeLabels()
  }

  /**
   * Put the name of each node on a ring on the OUTER side of the ring.
   *
   * ⚠ The renderer puts every label under its node, which is right for the graph. Round a ring
   * that puts each name on the next node, and thirteen names in one heap.
   */
  private placeLabels() {
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').each((d, i, texts) => {
      const chosen = this.nameSpots.get(d.id)
      if (chosen) {
        // Where `placeNames` put it: on the side of its glyph where there was room.
        d3.select(texts[i]).classed('inner', false).attr('x', chosen.x).attr('y', chosen.y).attr('text-anchor', chosen.anchor).attr('dy', '0.35em')
        return
      }
      if (d.type === 'account') {
        // Under the home icon, not where the graph's 50 px avatar puts it.
        d3.select(texts[i]).attr('dy', 27)
        return
      }
      if (d.type === 'location') {
        // A cloud location's name, under its icon.
        d3.select(texts[i]).attr('x', 0).attr('y', 0).attr('text-anchor', 'middle').attr('dy', CLOUD_PLACE_SIZE / 2 + 14)
        return
      }
      const spot = this.spots.get(d.id)
      // A node on an inner ring has the outer ring where its name would go: the name shows only
      // when the node is lit (`styles.ts`).
      d3.select(texts[i]).classed('inner', !!spot?.inner)
      const angle = spot?.angle
      if (angle === undefined) return
      const across = Math.cos(angle)
      const down = Math.sin(angle)
      const sideways = Math.abs(across) > 0.35
      d3.select(texts[i])
        .attr('x', across * LABEL_OFFSET)
        .attr('y', down * LABEL_OFFSET)
        .attr('text-anchor', !sideways ? 'middle' : across > 0 ? 'start' : 'end')
        // Beside the node: centred on it. Above: resting on it. Below: hanging from it.
        .attr('dy', sideways ? '0.35em' : down < 0 ? '-0.1em' : '0.8em')
    })
    this.placeChips()
    placeMarks(
      this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point'),
      this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label')
    )
  }

  /**
   * A chip behind the name of each node: a white pill with the name in body text (Benoit,
   * 2026-10-02, option A of four: "I am not a big fan of the halo"). The name of the selected node
   * is a pill in the colour of a node, so it stands out; the other names stay readable.
   *
   * ⚠ A chip is a `<rect>` in its own layer under the names: an SVG `<text>` holds no box. It
   * takes the size of its name (`getBBox`, in the name's own space, so its `x`, `y` and `dy`
   * count) and the name's classes, so a name that is hidden, lit, selected or offline hides,
   * lights, fills or dashes its chip in `styles.ts`. `tick` moves it with its node.
   * ⚠ Only the names of the nodes drawn now: a name that is fading out has no chip.
   */
  private placeChips() {
    // Every badge (`BADGE_TYPES`): a box under its icon and its name. ⚠ Not the name of a node on an
    // inner ring while it is not lit: its badge is hidden by `styles.ts`, and its icon is drawn.
    const drawn = new Set(this.nodes.filter(node => BADGE_TYPES.has(node.type)).map(node => node.id))
    const names = new Map<string, SVGTextElement>()
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').each((d, i, texts) => {
      if (drawn.has(d.id)) names.set(d.id, texts[i])
    })
    const chips = this.chips
      .selectAll<SVGRectElement, GraphNode>('rect.chip')
      .data(this.nodes.filter(node => names.has(node.id)), d => d.id)
    // ⚠ A chip fades in and out WITH its name. A chip drawn at once under a name that fades in
    // was an empty white pill for 400 ms: the flash Benoit saw. The fade is on the `opacity`
    // ATTRIBUTE, so no rule in `styles.ts` may set the `opacity` PROPERTY of a chip: a property
    // beats an attribute, and the chip would not fade (see `dimmed` below).
    // ⚠ `gone`: a chip that is going cuts no hole in the lines' mask (`placeHoles`).
    chips.exit().classed('gone', true).interrupt().transition().duration(this.exitDuration).attr('opacity', 0).remove()
    // ⚠ A chip that comes back while it fades out is in the update selection with its exit still
    // running, and the exit's `remove()` would take it: the badge came back with no box. Stop it.
    chips.filter(function () {
      return d3.active(this) !== null
    }).interrupt().attr('opacity', 1)
    const entered = chips.enter().append('rect').attr('opacity', 0)
    entered.transition().duration(TRANSITION_DURATION).attr('opacity', 1)
    // A badge is the thing: a click or a pointer on it is a click or a pointer on its node.
    this.bindPointer(entered)
    entered
      .merge(chips)
      .each((d, i, rects) => {
        const name = names.get(d.id)!
        const box = badgeOf(BADGE_ICONS[d.type]!, name.getBBox(), badgePad(d))
        const pill = d.type === 'node' || d.type === 'network'
        d3.select(rects[i])
          // The name's states, without `label`: the rules of a name (its colour) are not a chip's.
          // ⚠ `hidden` is `dimmed` on a chip: the global `.hidden` rule sets the opacity property.
          .attr(
            'class',
            [
              'chip',
              ...(name.getAttribute('class') ?? '')
                .split(' ')
                .filter(c => c && c !== 'label')
                .map(c => (c === 'hidden' ? 'dimmed' : c)),
            ].join(' ')
          )
          .attr('x', box.x)
          .attr('y', box.y)
          .attr('width', box.width)
          .attr('height', box.height)
          .attr('rx', pill ? box.height / 2 : 6)
          .attr('transform', `translate(${d.x ?? 0},${d.y ?? 0})${this.scaleOf(d) !== 1 ? ` scale(${this.scaleOf(d)})` : ''}`)
      })
    this.placeHoles()
    this.measure()
  }

  /**
   * A black rectangle in the lines' mask for each badge that is shown: the same box, corners and
   * place. ⚠ Not for a badge that is hidden (`visibility`, an inner ring's name, a dimmed service)
   * or going (`gone`): a line would end at nothing, and the hole of a badge that faded out stayed
   * in the mask until the next draw.
   */
  private placeHoles() {
    const shown = this.chips
      .selectAll<SVGRectElement, GraphNode>('rect.chip:not(.gone)')
      .filter(function () {
        return getComputedStyle(this).visibility !== 'hidden'
      })
      .nodes()
    // Keyed by the badge's node, so a hole stays with its badge from one draw to the next.
    const holes = this.holes.selectAll<SVGRectElement, SVGRectElement>('rect').data(shown, chip => d3.select<SVGRectElement, GraphNode>(chip).datum().id)
    holes.exit().remove()
    holes
      .enter()
      .append('rect')
      .attr('fill', 'black')
      .merge(holes)
      .each((chip, i, rects) => {
        const hole = d3.select(rects[i])
        ;['x', 'y', 'width', 'height', 'rx', 'transform'].forEach(name => hole.attr(name, chip.getAttribute(name)))
      })
  }

  /**
   * The box of each drawn thing: its glyph (with its count and its rings) and its label, as one box
   * from its middle, for `clearBoxes`. A name on a chip counts with the chip's padding.
   *
   * ⚠ A hidden label still counts, so a hover does not move the map under the pointer. ⚠ Except the
   * name of a node on an inner ring: it shows only when lit, and the outer ring is where it goes.
   * ⚠ After the labels and the chips are placed: a box is read from what is drawn (`getBBox`).
   */
  private measure() {
    this.boxes.clear()
    this.glyphs.clear()
    const drawn = new Set(this.nodes.map(node => node.id))
    const labels = new Map<string, SVGTextElement>()
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').each((d, i, texts) => {
      if (drawn.has(d.id)) labels.set(d.id, texts[i])
    })
    this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point').each((d, i, groups) => {
      if (!drawn.has(d.id)) return
      const icon = BADGE_ICONS[d.type]
      const glyph = icon ?? groups[i].getBBox()
      let box: Box = { x: glyph.x, y: glyph.y, width: glyph.width, height: glyph.height }
      this.glyphs.set(d.id, box)
      const text = labels.get(d.id)
      // A cloud location's box holds its name under it: nothing settles on the name.
      const under = d.type === 'location' && text ? text.getBBox() : undefined
      if (under && under.width && under.height) {
        const x = Math.min(box.x, under.x)
        const y = Math.min(box.y, under.y)
        box = {
          x,
          y,
          width: Math.max(box.x + box.width, under.x + under.width) - x,
          height: Math.max(box.y + box.height, under.y + under.height) - y,
        }
      }
      const name = text && !(d.type === 'node' && this.spots.get(d.id)?.inner) ? text.getBBox() : undefined
      // A label that is not laid out (no text yet) has an empty box at 0, 0: the glyph alone.
      if (icon && name && name.width && name.height) {
        // ⚠ A badge whose name is not placed yet is measured with its name where `placeNames` will
        // put it (`sideOf`): a new service is placed before its name is.
        const left = d.type === 'node' && sideOf(this.spots.get(d.id)?.angle ?? 0) === 'left'
        const side = this.nameSpots.has(d.id)
          ? name
          : left
            ? { x: icon.x - BADGE_GAP - name.width, y: -name.height / 2, width: name.width, height: name.height }
            : { x: icon.x + icon.width + BADGE_GAP, y: -name.height / 2, width: name.width, height: name.height }
        box = badgeOf(icon, side, badgePad(d))
      }
      this.boxes.set(d.id, box)
    })
  }

  // The joins below are `core.ts`'s, without the drag (a placed node is not dragged, and the map
  // takes the drag) and without the particles.
  //
  // ⚠ Every `.transition()` is preceded by `.interrupt()`, for the idle-tab hang `core.ts`
  // describes.

  private drawNode(d: GraphNode, group: d3.Selection<SVGGElement, unknown, null, undefined>) {
    const cluster = this.clusters.get(d.id)
    if (cluster) {
      this.drawCluster(d, group, cluster)
      return
    }
    this.renderer.node(d, group)
    // A node's dot is smaller on the map, so its badge is (`NODE_SCALE`). And it has no "Node" on
    // hover (Benoit, 2026-10-03: "no need for showing node on hover"): a compute node says only
    // what it runs.
    if (d.type === 'node') {
      const shapes = group.selectAll<SVGElement, unknown>(':scope > :not(title)')
      const shrink = group.insert('g', ':first-child').attr('class', 'shrink').attr('transform', `scale(${NODE_SCALE})`)
      shapes.each(function () {
        shrink.node()!.appendChild(this)
      })
      group.select('title.kind').remove()
      const hover = [d.compute, marksTitle(d.marks)].filter(Boolean).join('\n')
      if (hover) group.append('title').attr('class', 'kind').text(hover)
    }
    // ⚠ On the map the account is ALWAYS the home icon, the data center icon with a house: the
    // place of the viewer, not a person (Benoit, 2026-10-03: "I would prefer this home data center
    // icon instead of the user/org icon"). The picture is the graph's, and the drawer's.
    if (d.type === 'account') {
      group.selectAll(':scope > :not(title)').remove()
      this.drawHome(group)
    }
    // A network is a badge with a network icon: one box linked to two, white on the dark badge
    // (Benoit, 2026-10-03: "a better network icon like a computer network"). The graph's shield
    // read as a node.
    if (d.type === 'network') {
      group.selectAll(':scope > :not(title)').remove()
      const icon = group.append('g').attr('class', 'net-icon')
      icon.append('path').attr('d', NETWORK_ICON_LINES)
      icon.append('rect').attr('x', -2.4).attr('y', -6).attr('width', 4.8).attr('height', 3.6).attr('rx', 0.9)
      icon.append('rect').attr('x', -6.4).attr('y', 2.4).attr('width', 4.8).attr('height', 3.6).attr('rx', 0.9)
      icon.append('rect').attr('x', 1.6).attr('y', 2.4).attr('width', 4.8).attr('height', 3.6).attr('rx', 0.9)
    }
    // A location that is drawn open: the data center glyph in place of the graph's dot, small, in
    // the middle of its ring. The renderer has set the classes and the title, which are kept.
    // A cloud location is its cloud's icon (`CloudPlace`), and its hover names it with its count.
    if (d.type === 'location') {
      group.selectAll(':scope > :not(title)').remove()
      if (d.cloudPlace) {
        drawCloudPlace(group, d.cloudPlace)
        const count = this.floatCount(d)
        group.attr('data-nodes', count)
        group.select('title.kind').remove()
        if (count) group.append('title').attr('class', 'kind').text(`${d.label}: ${count} ${count === '1' ? 'node' : 'nodes'}. Click to hide them.`)
      } else this.drawDataCenter(group)
    }
    // The user icon is the icon of its own place: it carries the count of the nodes it stands for
    // while their group is too busy to draw open (`Cluster.account`).
    const riding = this.riding.get(d.id)
    group.attr('data-riding', riding ? this.countOf(riding) : null)
    group.attr('data-arcs', riding ? (this.ridingArcs(riding) ?? null) : null)
    if (riding) {
      this.drawCount(group, this.countOf(riding), 13, -13)
      // The nodes of the home icon's place are counted on it, so it takes their colors (app#498).
      this.renderer.arcs(this.ridingArcs(riding), group, CLOSED_ARC_RADIUS)
      group.select('title.kind').remove()
      group
        .append('title')
        .attr('class', 'kind')
        .text(`${this.countWords(riding)} here. Click to ${riding.folded ? 'show them' : 'zoom in'}.`)
    }
    const atViewer = !!this.spots.get(d.id)?.atViewer
    // ⚠ After `renderer.node`, which sets the whole `class`. `updateNodes` reads this back to know
    // what the node was drawn as.
    group.classed('at-viewer', atViewer)
    if (atViewer) {
      group.append('circle').attr('class', 'viewer-ring').attr('r', d.type === 'node' ? 11 : 9)
      group.select('title.kind').remove()
      group.append('title').attr('class', 'kind').text('No location on record. Shown at your location.')
    }
  }

  /**
   * The data center glyph: a box with three racks, each with its light.
   *
   * ⚠⚠ EVERY LOCATION ON THE MAP IS A DATA CENTER ICON (Benoit, 2026-10-02, nobgp/app#449): "the
   * location nodes should be data center icons always, they do not show their nodes (just a count)
   * when it's too crowded. the current location is the org/user icon instead". An open location
   * is this glyph in the middle of its ring of nodes. A closed one, or several that are too
   * crowded, is this glyph with a count.
   *
   * ⚠ The glyph is drawn here and not by the renderer: the graph draws a location as a dot.
   */
  private drawDataCenter(group: d3.Selection<SVGGElement, unknown, null, undefined>) {
    // 26 px across (Benoit chose it, 2026-10-02: the first one, 36 px, was "a little big").
    const glyph = group.append('g').attr('class', 'datacenter')
    glyph.append('rect').attr('class', 'box').attr('x', -13).attr('y', -13).attr('width', 26).attr('height', 26).attr('rx', 6.5)
    const racks = glyph.append('g').attr('class', 'racks')
    ;[-6.5, -1.8, 2.9].forEach(y => {
      racks.append('rect').attr('x', -7.2).attr('y', y).attr('width', 14.4).attr('height', 3.6).attr('rx', 1.1)
      racks.append('circle').attr('cx', -4.7).attr('cy', y + 1.8).attr('r', 0.8)
    })
    return glyph
  }

  /**
   * The viewer's place: the data center icon, with a small house at its bottom right corner. First
   * for an account with no picture (Benoit, 2026-10-02: "if there is no user/org icon we should use
   * the data center icon, but make it a bit different (with a home in it or different color)"),
   * and since 2026-10-03 for every account ("I would prefer this home data center icon instead of
   * the user/org icon").
   *
   * ⚠ The racks are the same as on a location, at the same size, and only the house is different
   * (Benoit, 2026-10-02, nobgp/app#455: a location pin first, then "a home badge that is not in a
   * circle like the pin badge"). The house is at the bottom right because the count of the nodes
   * is at the top right (`drawCount`).
   *
   * The house is a line in the noBGP orange, with no door. Its right wall goes on up through the
   * roof a little, and that is the chimney (Benoit chose it from mockups, 2026-10-02).
   */
  private drawHome(group: d3.Selection<SVGGElement, unknown, null, undefined>) {
    const glyph = this.drawDataCenter(group).classed('home', true)
    const house = glyph.append('g').attr('class', 'house').attr('transform', 'translate(11,10)')
    // The inside of the house, so that the border of the box does not show through it.
    house.append('path').attr('class', 'fill').attr('d', 'M-4.4,-1.8 L0,-5.8 L4.4,-1.8 V5 H-4.4 Z')
    // The roof with its eaves, then the walls: the right wall is also the chimney. The line is drawn
    // twice: first wide in the colour of the paper, as an edge that keeps it apart from the box.
    const line = 'M-6.4,-0.2 L0,-6 L6.4,-0.2 M-4.4,-1.8 V5 H4.4 V-4.8'
    house.append('path').attr('class', 'edge').attr('d', line)
    house.append('path').attr('class', 'line').attr('d', line)
  }

  /** The arcs of the nodes that the home icon counts. */
  private ridingArcs(cluster: Cluster): string | undefined {
    return mergeArcs(cluster.nodes.map(id => this.adapter.nodeMap.get(id)?.arcs))
  }

  /** A location, or several, that is too crowded to draw open at this zoom. See `layoutView`. */
  private drawCluster(d: GraphNode, group: d3.Selection<SVGGElement, unknown, null, undefined>, cluster: Cluster) {
    group
      .attr('class', 'point location cluster')
      .classed('active', d.active)
      .classed('selected', !!d.selected)
      .classed('streaming', !!d.streaming)
      .classed('connected', d.connected)
      .classed('offline', d.offline)
    if (cluster.cloudPlace) {
      // A cloud location that the reader closed: its icon, with the count at its corner.
      const corner = CLOUD_PLACE_SIZE / 2
      drawCloudPlace(group, cluster.cloudPlace)
      this.drawCount(group, d.label, corner, -corner)
      this.renderer.arcs(d.arcs, group, corner + 6)
      group.append('title').attr('class', 'kind').text(`${NODE_MARK_TITLES[cluster.cloudPlace]}: ${this.countWords(cluster)}. Click to show them.`)
      return
    }
    this.drawDataCenter(group)
    this.drawCount(group, d.label, 13, -13)
    this.renderer.arcs(d.arcs, group, CLOSED_ARC_RADIUS)
    const places = cluster.hubs.length
    group
      .append('title')
      .attr('class', 'kind')
      .text(`${this.countWords(cluster)}${places > 1 ? ` in ${places} locations` : ''}. Click to ${cluster.folded ? 'show them' : 'zoom in'}.`)
  }

  /** What a drag and a right-click do on this glyph, for its hover, or undefined. */
  private moveHint(d: GraphNode): string | undefined {
    const target = this.moveTarget(d)
    if (!target) return undefined
    const drag =
      target.kind === 'cloud'
        ? 'Drag to put it where every member of the org sees it.'
        : `Drag to move ${target.nodeIds.length === 1 ? 'this node' : `these ${target.nodeIds.length} nodes`} to where ${target.nodeIds.length === 1 ? 'it is' : 'they are'}.`
    if (!this.isSet(target)) return drag
    return `${drag}\nPlaced by hand. Right-click to ${target.kind === 'cloud' ? 'let the map place it again' : 'put it back at the place of its address'}.`
  }

  /** How many nodes an open cloud location holds, as its title says it, or null for anything else. */
  private floatCount(d: GraphNode): string | null {
    const float = d.cloudPlace ? this.plan?.floats.find(one => one.id === d.id) : undefined
    return float ? String(float.nodes.length) : null
  }

  /** The count on an icon: the nodes it stands for. See `clusterCount`. */
  private countOf(cluster: Cluster): string {
    return clusterCount(cluster)
  }

  /** The same in words, for a hover: "13 nodes". */
  private countWords(cluster: Cluster): string {
    return `${cluster.count} ${cluster.count === 1 ? 'node' : 'nodes'}`
  }

  /**
   * The count of the nodes that an icon stands for, at its top right corner (`clusterCount`).
   *
   * ⚠ No state of the icon changes the count (`styles.ts`): a lit icon drew it bold.
   */
  private drawCount(group: d3.Selection<SVGGElement, unknown, null, undefined>, text: string, x: number, y: number) {
    const half = Math.max(9, 4 + text.length * 3.4)
    const badge = group.append('g').attr('class', 'count').attr('transform', `translate(${x},${y})`)
    badge.append('rect').attr('x', -half).attr('y', -9).attr('width', half * 2).attr('height', 18).attr('rx', 9)
    badge.append('text').attr('text-anchor', 'middle').attr('dy', '0.35em').text(text)
  }

  /**
   * What a click and a pointer do on a node: on its glyph, and on its badge (`placeChips`), which
   * is part of it.
   */
  private bindPointer<E extends Element>(selection: d3.Selection<E, GraphNode, d3.BaseType, unknown>) {
    selection
      .on('click', (event, d) => {
        // ⚠ The map under the node must not also read this as a click on empty map, which closes
        // the panel the click opens.
        event.stopPropagation()
        // A location is not a node of the app: a click toggles its nodes. See `toggle`.
        if (this.toggle(d)) return
        this.emit('click', d, event)
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault()
        // ⚠ On a location, the home icon or a cloud location that a person placed, a right-click
        // asks to put it back (nobgp-router#1976). Anywhere else it opens or closes, as a click does.
        const target = this.moveTarget(d)
        if (target && this.isSet(target)) {
          if (target.kind === 'cloud') this.emit('resetCloud', { cloud: target.cloud })
          else this.emit('resetNodes', { nodeIds: target.nodeIds, label: target.label })
          return
        }
        if (!this.toggle(d)) this.emit('click', d, event)
      })
      .on('mouseenter', (event, d) => {
        clearTimeout(this.leaveTimeout)
        // What a drag and a right-click do here (nobgp-router#1976), on the line under the title.
        const hint = this.moveHint(d)
        if (hint) {
          const group = d3.select(event.currentTarget as SVGGElement)
          const title = group.select<SVGTitleElement>('title.kind').empty() ? group.append('title').attr('class', 'kind') : group.select<SVGTitleElement>('title.kind')
          const base = (title.text() ?? '').split('\n').filter(line => !line.startsWith('Drag ') && !line.startsWith('Placed by hand')).join('\n')
          title.text([base, hint].filter(Boolean).join('\n'))
        }
        if (this.clusters.has(d.id)) return
        if (d.expanded === false) return
        this.emit('enter', d.type === 'service' ? d.id : (d.modelNodeId ?? d.id), event)
      })
      .on('mouseleave', (event, d) => {
        if (d.type === 'node' || d.type === 'service') {
          this.leaveTimeout = setTimeout(() => this.emit('leave', d, event), LEAVE_DELAY)
        } else {
          this.emit('leave', d, event)
        }
      })
  }

  private updateNodes(data: GraphNode[]) {
    const nodes = this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point').data(data, d => d.id)

    nodes.exit().style('pointer-events', 'none').interrupt().transition().duration(this.exitDuration).attr('opacity', 0).remove()

    const nodeEnter = nodes
      .enter()
      .append('g')
      .attr('opacity', 0)
      .each((d, i, groups) => this.drawNode(d, d3.select(groups[i])))
    this.bindPointer(nodeEnter)
    nodeEnter.call(this.dragBehavior())

    nodes.each((d, i, groups) => {
      const group = d3.select(groups[i])
      // ⚠⚠ ALSO WHEN THE NODE'S PLACE CAME OR WENT, which `stale` does not know: `stale` is about
      // the node's own fields, and a place is a `node_metadata` row. After a switch of org, a
      // change of Show Offline, or a new node, the nodes are drawn BEFORE their rows are read, so
      // each is drawn at the viewer's place with the dashed ring. The rows arrive, the node moves
      // to its real place, and it kept the ring and "No location on record" until a reload.
      const atViewer = !!this.spots.get(d.id)?.atViewer
      // And when the count on the user icon came, went or changed.
      const riding = this.riding.get(d.id)
      const sameCount =
        group.attr('data-riding') === (riding ? this.countOf(riding) : null) &&
        group.attr('data-arcs') === ((riding && this.ridingArcs(riding)) ?? null)
      // And when a cloud location's count of nodes changed: its title says it.
      const sameFloat = group.attr('data-nodes') === this.floatCount(d)
      if (!d.stale && group.classed('at-viewer') === atViewer && sameCount && sameFloat) return
      group.selectAll('*').remove()
      this.drawNode(d, group)
    })

    nodeEnter
      .merge(nodes)
      .style('pointer-events', null)
      .interrupt()
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('opacity', 1)
  }

  /**
   * The renderer's label, with a service's name shortened: its node's name is not repeated in front
   * (`withoutNodeName`). A search match in the name moves with it.
   */
  private drawLabel(d: GraphNode, group: d3.Selection<SVGTextElement, unknown, null, undefined>) {
    const label = d.label ?? ''
    const short = d.type === 'service' ? withoutNodeName(label, this.anchors.get(d.id)?.[0]?.label) : label
    if (short === label) {
      this.renderer.label(d, group)
      return
    }
    const cut = label.length - short.length
    const matchRanges = d.matchRanges
      ?.map(([start, end]) => [Math.max(0, start - cut), Math.max(0, end - cut)] as [number, number])
      .filter(([start, end]) => end > start)
    this.renderer.label({ ...d, label: short, matchRanges }, group)
  }

  private updateLabels(data: GraphNode[]) {
    const labels = this.renderer.labelContainer
      .selectAll<SVGTextElement, GraphNode>('.label')
      .data(
        // No label on a location: the map names the place. ⚠ A cloud location has its name: no
        // place names it. Not when closed: its label is then its count, at its corner.
        data.filter(d => d.type !== 'location' || (!!d.cloudPlace && !this.clusters.has(d.id))),
        d => d.id
      )

    labels.exit().interrupt().transition().duration(this.exitDuration).attr('opacity', 0).remove()

    const labelEnter = labels
      .enter()
      .append('text')
      .attr('opacity', 0)
      .each((d, i, groups) => this.drawLabel(d, d3.select(groups[i])))

    labels.each((d, i, groups) => {
      if (!d.stale) return
      const group = d3.select(groups[i])
      group.selectAll('*').remove()
      this.drawLabel(d, group)
    })

    labelEnter.merge(labels).interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 1)
  }

  /**
   * The graph's line, with the map's own rule for what is solid: a network's line never is, and
   * a location's line to its node always is. See `solidLink`.
   */
  private drawLink(d: GraphLink, line: d3.Selection<SVGLineElement, unknown, null, undefined>) {
    this.renderer.link(d, line)
    line.classed('connected', solidLink(d))
  }

  private updateLinks(data: GraphLink[]) {
    const links = this.renderer.linkContainer.selectAll<SVGLineElement, GraphLink>('.link').data(data, d => d.id)

    links.exit().interrupt().transition().duration(this.exitDuration).attr('stroke-opacity', 0).remove()

    const linkEnter = links
      .enter()
      .append('line')
      .each((d, i, lines) => this.drawLink(d, d3.select(lines[i])))
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 0)

    links.each((d, i, lines) => {
      if (d.stale) this.drawLink(d, d3.select(lines[i]))
    })

    linkEnter.merge(links).interrupt().transition().duration(TRANSITION_DURATION).attr('stroke-opacity', 1)
  }

  /** The invisible wide line from a node to its service, which keeps a hover alive on the way. */
  private updateBridges(data: GraphLink[]) {
    const bridges = this.renderer.bridgeContainer
      .selectAll<SVGLineElement, GraphLink>('.bridge')
      .data(
        data.filter(d => d.expanded && d.targetType === 'service'),
        d => d.id
      )

    bridges.exit().remove()

    bridges
      .enter()
      .append('line')
      .attr('class', 'bridge')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 24)
      .attr('stroke-linecap', 'round')
      .style('pointer-events', 'stroke')
      .on('mouseenter', () => clearTimeout(this.leaveTimeout))
      .on('mouseleave', (event, d) => {
        this.leaveTimeout = setTimeout(() => this.emit('leave', d.source as GraphNode, event), LEAVE_DELAY)
      })
  }

  /**
   * Work out the least radius of each ring from the badges of its nodes (`ringFit`). True when one
   * changed, and the map must be laid out again.
   *
   * ⚠ With `all` false (a hover, a selection, a realtime update), only a ring with a node that is
   * new. A selected name is bolder and so wider, and a ring fitted to it moved its nodes under the
   * pointer (review of app#455).
   */
  private fitRings(all: boolean): boolean {
    const plan = this.plan
    if (!plan) return false
    const rings = new Map<string, string[]>([...plan.hubs, ...plan.floats].map(hub => [hub.id, hub.nodes]))
    if (plan.account) rings.set(plan.account.id, plan.account.around.map(node => node.id))
    let changed = false
    const drawn = new Set(this.nodes.map(node => node.id))
    rings.forEach((ids, ring) => {
      if (!ids.length) return
      // ⚠ Again when its nodes changed, though each was drawn before: a node that joins a cloud
      // location (a task opens) or leaves it was drawn, and the ring kept the size it had without it.
      const members = ids.join(' ')
      if (!all && this.fits.min.has(ring) && this.ringMembers.get(ring) === members && ids.every(id => this.settled.has(id) || !drawn.has(id))) return
      this.ringMembers.set(ring, members)
      // ⚠ A ring that is not drawn (its group is a data center icon) is fitted too, from its names,
      // generously. A click zooms to where the group opens (`openZoomOf`), and a ring measured only
      // once it was open came out wider, made the group busy, and closed it again.
      const seats = ringOffsets(ids.length, 1)
      const boxes = ids.map((id, i) => this.boxes.get(id) ?? estimateBadge(this.adapter.nodeMap.get(id)?.label ?? '', sideOf(seats[i].angle)))
      const fit = ringFit(boxes, 0)
      const same =
        Math.abs((this.fits.min.get(ring) ?? 0) - fit.radius) < 1 &&
        Math.abs((this.fits.reach.get(ring) ?? 0) - fit.reach) < 1 &&
        ids.every((id, i) => (this.fits.out.get(id) ?? 0) === fit.extra[i])
      if (same) return
      this.fits.min.set(ring, fit.radius)
      this.fits.reach.set(ring, fit.reach)
      ids.forEach((id, i) => this.fits.out.set(id, fit.extra[i]))
      this.fitsVersion++
      changed = true
    })
    return changed
  }

  /** How hard the `outX` and `outY` forces pull: a network to its goal, nothing else. */
  private pullOf(d: GraphNode): number {
    return this.goals.has(d.id) ? NETWORK_PULL : 0
  }


  /**
   * A force: nothing that is drawn lies on another (Benoit, 2026-10-03). Each pair of boxes
   * (`measure`) that overlap is moved apart, the shorter way out, with `BOX_GAP` between them.
   * `clearOf` has the rule and its test.
   *
   * ⚠ A location and the user icon are pinned to real places, and a service that was
   * drawn stays where it is (`still`): the other one moves by the whole overlap. Two that move share it, half each, and a node's seat pulls it back. Two pinned ones
   * are left: `layoutView` keeps them apart, or makes them one data center icon.
   * ⚠ At the position the next tick gives each (`x + vx`), as d3's own collide force does: the
   * other forces have pushed them this tick, and a check at `x` would let those win.
   */
  private clearBoxes() {
    if (!this.boxes.size) return
    const at = (d: GraphNode) => ({ x: d.x! + (d.vx ?? 0), y: d.y! + (d.vy ?? 0) })
    // Pinned to a real place, or a service that stays where it is (`still`).
    const fixed = (d: GraphNode) => d.fx != null
    const half = BOX_GAP / 2
    const drawn = this.nodes
      .filter(node => this.boxes.has(node.id) && node.x !== undefined && node.y !== undefined)
      .map(node => {
        const box = grow(this.boxes.get(node.id)!, this.scaleOf(node))
        return { node, box: { x: box.x - half, y: box.y - half, width: box.width + BOX_GAP, height: box.height + BOX_GAP } }
      })
    drawn.forEach((one, i) => {
      for (let j = i + 1; j < drawn.length; j++) {
        const two = drawn[j]
        const still = [fixed(one.node), fixed(two.node)]
        if (still[0] && still[1]) continue
        const move = clearOf(one.box, at(one.node), two.box, at(two.node))
        if (!move) continue
        const share = still[0] ? 0 : still[1] ? 1 : 0.5
        one.node.vx = (one.node.vx ?? 0) + move.dx * share
        one.node.vy = (one.node.vy ?? 0) + move.dy * share
        two.node.vx = (two.node.vx ?? 0) - move.dx * (1 - share)
        two.node.vy = (two.node.vy ?? 0) - move.dy * (1 - share)
      }
    })
  }

  /**
   * Put a node's new services in ONE COLUMN beside its badge (Benoit, 2026-10-03, over a long
   * exchange on app#457): in name order; beside the name end,
   * else beside the dot; centred on the node's row, slid away from the ring when that has no room,
   * never past the row; moved out only when no slide fits; with `LEAF_MIN_GAP` at its sides and
   * `LEAF_END_GAP` above and below from every badge. It is placed once and stays there while its
   * node is the one in focus (`still`), until one joins the column or leaves it (`settle`). Only when no column fits anywhere does each one look for
   * room on its own (`findRoom`).
   */
  private placeLeaves(fresh: GraphNode[]) {
    if (!fresh.length) return
    const isNew = new Set(fresh.map(node => node.id))
    const taken: Rect[] = []
    this.nodes.forEach(node => {
      const box = this.boxes.get(node.id)
      if (box && !isNew.has(node.id) && node.x !== undefined && node.y !== undefined) taken.push(rectAt(grow(box, this.scaleOf(node)), { x: node.x, y: node.y }, BOX_GAP))
    })
    // ⚠ The name of a node on an inner ring is not in its box (`measure`: it shows only when lit), and
    // it IS shown while its services are: it is in the way of its own column.
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').each((d, i, texts) => {
      if (d.type !== 'node' || !this.spots.get(d.id)?.inner || !this.adapter.focusIds.has(d.modelNodeId ?? d.id)) return
      const name = texts[i].getBBox()
      if (name.width && d.x !== undefined && d.y !== undefined) taken.push(rectAt(grow(name, this.scaleOf(d)), { x: d.x, y: d.y }, BOX_GAP))
    })
    // Each node's (or group of nodes') new services, in name order.
    const groups = new Map<string, GraphNode[]>()
    fresh.sort(byName).forEach(node => {
      const key = this.leafKey(node)
      groups.set(key, [...(groups.get(key) ?? []), node])
    })
    groups.forEach(group => {
      const on = this.leafAnchors(group[0])
      if (!on.length) return
      // From the name end of a single node's badge, where its line starts (`endOf`): from the dot,
      // a node on the left half of a ring had its own badge on its outward side.
      // ⚠ The name end itself, not `endOf`: the new ones have no place yet to be nearer to.
      const centre = this.nameEnd(on[0]) ?? { x: on[0].x!, y: on[0].y! }
      // ⚠ A COLUMN, read from top to bottom in name order, beyond the name end of the badge on its
      // outward side (Benoit, 2026-10-03: "spread the services ... toward the outside", "not as
      // spread when they could be closer together", then "the order would be more natural if from
      // top to bottom"). On the right of a ring it starts at the badge end and goes right; on the
      // left it ends there; straight up or down, it is centred above or below. The lines still fan
      // out from the badge end. A fan round an arc was spread wide above and below a ring, where
      // two badges side by side need a wide angle, and it was pushed far out to fit.
      const outward = this.outwardOf(on, centre)
      // As large as the node it hangs off (`groupScale`).
      const size = this.spots.get(on[0].id)?.scale ?? 1
      // In name order: `group` is sorted already.
      const items = group.map(node => {
        const box = grow(this.boxes.get(node.id) ?? this.glyphs.get(node.id) ?? { x: -12, y: -12, width: 24, height: 24 }, size)
        return { node, box, bearing: outward }
      })
      const height = items.reduce((sum, one) => sum + one.box.height + LEAF_ROW_GAP * size, -LEAF_ROW_GAP * size)
      // ⚠ CENTRED on the node's row (Benoit, 2026-10-03: "center the service stack vertically such
      // that the line of the row is the middle of the stack, if even, between two services, if odd
      // the middle service is on the same row as the node"): the middle one, or the gap between the
      // two middle ones, is on the row. When that has no room it slides away from the ring, up for a
      // node in the upper part of its ring, down in the lower part, either way near 3 and 9 o'clock
      // (`lift`, signed).
      const seat = Math.sin(this.spots.get(on[0].id)?.angle ?? 0)
      const tops: number[] = []
      items.reduce((y, one) => {
        tops.push(y)
        return y + one.box.height + LEAF_ROW_GAP * size
      }, 0)
      const k = Math.floor(items.length / 2)
      const middle =
        items.length % 2
          ? tops[k] + items[k].box.height / 2
          : (tops[k - 1] + items[k - 1].box.height + tops[k]) / 2
      const columnAt = (from: { x: number; y: number }, bearing: number, distance: number, lift = 0) => {
        const across = Math.cos(bearing)
        const down = Math.sin(bearing)
        const anchor = { x: from.x + distance * across, y: from.y + distance * down }
        // Straight up or down (the last resort): the column grows away from the node. Beside it: centred.
        let top =
          Math.abs(down) > LEAF_VERTICAL ? (down < 0 ? anchor.y - height : anchor.y) : anchor.y - middle + lift
        return items.map(one => {
          const left =
            Math.abs(across) < 1 - LEAF_VERTICAL ? anchor.x - one.box.width / 2 : across > 0 ? anchor.x : anchor.x - one.box.width
          const at = { x: left - one.box.x, y: top - one.box.y }
          top += one.box.height + LEAF_ROW_GAP * size
          return { ...one, at }
        })
      }
      // ⚠ The whole column at ONE distance, the nearest at which it lies on nothing.
      // ⚠ A hair of margin of its own, past the others' `BOX_GAP`: two rows of badges are 4 px apart,
      // and a full margin each side kept a column off its node's row whenever a badge was below.
      // ⚠ And the least gap at the SIDES from every other badge, not only from its own node's
      // (Benoit, 2026-10-03: "the services column in that example is too close to the
      // desktop-n88irnv node"). Above and below, the hair: rows of badges are 4 px apart.
      const side = LEAF_MIN_GAP * size - BOX_GAP
      const end = LEAF_END_GAP * size - BOX_GAP
      const fits = (column: ReturnType<typeof columnAt>) =>
        column.every(one => {
          const rect = rectAt(one.box, one.at, 0.5)
          // ⚠ And a least gap above and below the stack from what is near (Benoit, 2026-10-03:
          // "ensuring a minimum distance [between] the top or bottom of the stack and obstacles").
          const wide = { left: rect.left - side, right: rect.right + side, top: rect.top - end, bottom: rect.bottom + end }
          return taken.every(other => !overlapArea(wide, other))
        })
      // ⚠⚠ SEVERAL PLACES, AND THE NEAREST THAT FITS (Benoit, 2026-10-03: "why are CM4's lines to
      // its services way up when they should [be] closer to the right of the node"). Outward from
      // the name end first; then beside the name end, away from the badge; then beside the dot, on
      // the other side. Straight above a ring, outward ran into the ring above, and the column went
      // far up while the room beside the node was empty. A line meets the badge at its nearer end
      // (`endOf`), so a column beside the dot gets its lines from the dot.
      const owner = on[0]
      const away = owner.type === 'node' && sideOf(this.spots.get(owner.id)?.angle ?? 0) === 'left' ? Math.PI : 0
      const places =
        on.length === 1
          ? [
              // ⚠ BESIDE the badge only, never over it (Benoit, 2026-10-03: "the left of the service
              // stack should not be to the left of the right edge of the node"): beside the name
              // end, then beside the dot. Straight out from a node at 12 o'clock put the column over
              // the badge; it is the last resort, when neither side has room.
              { from: centre, bearing: away, extra: 0 },
              // The dot side costs more: the name side is where the reader looks for them.
              { from: { x: owner.x!, y: owner.y! }, bearing: away + Math.PI, extra: DOT_SIDE_COST * size },
            ]
          : [{ from: centre, bearing: outward, extra: 0 }]
      // Each place at each distance and lift, cheapest first. ⚠ A lift costs far more than a step
      // out (Benoit, 2026-10-03: "the service stack should be more to the right ... such that the
      // last service is on the same line as the node"): the column stays on the node's row and
      // moves out, and leaves the row only when no distance has room.
      const row = (items[0]?.box.height ?? 20) + LEAF_ROW_GAP * size
      // Away from the ring: up in the upper part, down in the lower part, either way at the sides.
      const ways = seat < -LEAF_ROW_SIDE ? [-1] : seat > LEAF_ROW_SIDE ? [1] : [-1, 1]
      // ⚠ SLIDE FIRST, MOVE OUT ONLY WHEN NO SLIDE FITS (Benoit, 2026-10-03: "move up or down the
      // service stack to fit while keeping the correct column distance ... it can still fail in
      // which case, there is no choice than move the column out more"). At each distance, nearest
      // first: centred, then a quarter row at a time away from the ring, until an end of the stack is
      // on the node's row.
      // ⚠ NEVER PAST THE NODE'S ROW (Benoit, 2026-10-03: "you could not have slid the stack up or down
      // without touching the desktop node, so this should have moved out"): a slide stops when the
      // last one (up) or the first one (down) is on the row. Further, the stack jumped over the
      // badge below, and its lines crossed it.
      const maxUp = height - (items[items.length - 1]?.box.height ?? 0) / 2 - middle
      const maxDown = middle - (items[0]?.box.height ?? 0) / 2
      const lifts = [0, ...Array.from({ length: 4 * (items.length + 4) }, (_, i) => (i + 1) / 4).flatMap(rows => ways.map(way => way * rows))]
        .map(lift => lift * row)
        .filter(lift => lift <= maxDown + 0.5 && -lift <= maxUp + 0.5)
      const tries = LEAF_FAN.flatMap(distance => lifts.map(lift => ({ distance: distance * size, lift })))
      // `tries` is in order: nearest distance first, then the least slide. So the first that fits is
      // a place's best, and the places are compared by how far out that is (and what the place costs).
      const nearest = (candidates: typeof places) => {
        let found: { place: (typeof places)[number]; distance: number; lift: number } | undefined
        for (const place of candidates) {
          const hit = tries.find(one => fits(columnAt(place.from, place.bearing, one.distance, one.lift)))
          if (hit && (!found || hit.distance + place.extra < found.distance + found.place.extra)) found = { place, ...hit }
        }
        return found
      }
      const best = nearest(places) ?? (on.length === 1 ? nearest([{ from: centre, bearing: outward, extra: 0 }]) : undefined)
      const radius = best?.distance
      const placed = columnAt(best?.place.from ?? centre, best?.place.bearing ?? outward, radius ?? LEAF_FAN[0] * size, best?.lift ?? 0)
      const along = (box: Box, bearing: number) => (box.width / 2) * Math.abs(Math.cos(bearing)) + (box.height / 2) * Math.abs(Math.sin(bearing))
      placed.forEach(one => {
        // No distance fits the fan: each one looks for room near its own bearing.
        const at =
          radius !== undefined
            ? one.at
            : findRoom(one.box, centre, one.bearing, taken, LEAF_RADII.map(r => r + along(one.box, one.bearing)), LEAF_STEPS, false, 0.5)
        one.node.x = at.x
        one.node.y = at.y
        taken.push(rectAt(one.box, at, BOX_GAP))
      })
    })
  }

  /** The node a service is placed round, the one it hangs off, when it has a place. */
  private leafAnchors(node: GraphNode): GraphNode[] {
    return (this.anchors.get(node.id) ?? []).filter(anchor => anchor.x !== undefined && anchor.y !== undefined)
  }

  /** Which node a service is placed round, as a key (`placeLeaves`, `still`). */
  private leafKey(node: GraphNode): string {
    return this.leafAnchors(node)
      .map(anchor => anchor.id)
      .sort()
      .join(' ')
  }

  /**
   * The bearing away from the middle of the ring(s) of `nodes`, as seen from `from`: the side where
   * there is room. ⚠ From the name end of a node's badge, not its dot (Benoit, 2026-10-03: "we
   * could position as if the node is on the other side of the badge"): the services go on along the
   * line from the data center through the badge, instead of curling back beside it. A node with no
   * ring gives 12 o'clock.
   */
  private outwardOf(nodes: GraphNode[], from: { x: number; y: number }): number {
    const middles = nodes.map(node => this.ringMiddle(node)).filter((at): at is { x: number; y: number } => !!at)
    if (!middles.length) return -Math.PI / 2
    const middle = { x: d3.mean(middles, at => at.x)!, y: d3.mean(middles, at => at.y)! }
    if (Math.hypot(from.x - middle.x, from.y - middle.y) < 1) return -Math.PI / 2
    return Math.atan2(from.y - middle.y, from.x - middle.x)
  }

  /** Where the middle of a node's ring is drawn: its location, or the home icon. */
  private ringMiddle(node: GraphNode): { x: number; y: number } | undefined {
    const plan = this.plan
    if (!plan) return undefined
    const hub = [...plan.hubs, ...plan.floats].find(one => one.nodes.includes(node.id))
    const id = hub ? hub.id : plan.account?.around.some(one => one.id === node.id) ? plan.account.id : undefined
    return id ? this.spots.get(id) : undefined
  }


  /**
   * Put each badge's name beside its icon: right of it, or left of it for a node on the left half
   * of a ring (`sideOf`). The side follows from where the badge is, so it never hops; what keeps the
   * badges apart is where they are (`fitRings`, `placeLeaves`, `clearBoxes`).
   */
  private placeNames() {
    const texts = new Map<string, SVGTextElement>()
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').each((d, i, all) => texts.set(d.id, all[i]))
    const spots = new Map<string, LabelSpot>()
    this.nodes.forEach(node => {
      const icon = BADGE_ICONS[node.type]
      // ⚠ Not the name of a node on an inner ring: it shows only when lit, where `placeLabels` puts it.
      if (!icon || !texts.has(node.id) || (node.type === 'node' && this.spots.get(node.id)?.inner)) return
      const size = texts.get(node.id)!.getBBox()
      if (!size.width || !size.height) return
      // A node on a ring has its name away from the middle, so its dot is the end nearer the data
      // center (`sideOf`; Benoit, 2026-10-03, "dot toward the center"). Every other badge has its
      // icon on the left ("let's try with the icon on the left of the name for all badges").
      const ringAngle = node.type === 'node' ? this.spots.get(node.id)?.angle : undefined
      const side = ringAngle === undefined ? 'right' : sideOf(ringAngle)
      const spot = labelSpots(icon, size.width, size.height, BADGE_GAP, { x: 0, y: 0 }).find(one => one.side === side)!
      spots.set(node.id, { ...spot, box: badgeOf(icon, spot.box, badgePad(node)) })
    })
    this.nameSpots = spots
    this.placeLabels()
  }


  /**
   * Where the dot would be at the name end of a badge, or nothing when its name is not drawn. The
   * dot is as far in from its own end as this point is from the other one: the dot is at 0, so the
   * point is the sum of the two edges.
   */
  private nameEnd(d: GraphNode): { x: number; y: number } | undefined {
    const spot = this.nameSpots.get(d.id)
    return spot && { x: (d.x ?? 0) + (2 * spot.box.x + spot.box.width) * this.scaleOf(d), y: d.y ?? 0 }
  }

  /**
   * Where a line between `d` and `other` meets `d`: its middle, or, for a node's line to a service,
   * the end of the node's badge nearer `other`: its dot, or the point as far in from the name end
   * as the dot is from its own end (Benoit, 2026-10-03: "have the lines from the node to services
   * ... initiate from the other side of the badge instead of the icon side"). A line from the dot ran under the name. A node whose name is not
   * drawn (an inner ring) keeps its dot.
   */
  private endOf(d: GraphNode, other: GraphNode): { x: number; y: number } {
    const at = { x: d.x ?? 0, y: d.y ?? 0 }
    // ⚠ Not the edge: where the dot would be if it were at that end (Benoit, 2026-10-03: "make the
    // lines start for the center of the circle if it was at the far end of the badge", `nameEnd`).
    // ⚠ The same for a service's badge, at the node end of its line (Benoit, 2026-10-03:
    // "on the left of the ring the lines going from the node to the service ... appear to connect to
    // the opposite side"): its icon is at its left end, and from a node on its right the line ran
    // under the whole badge to it.
    const paired = (d.type === 'node' && other.type === 'service') || (d.type === 'service' && other.type === 'node')
    const far = paired ? this.nameEnd(d) : undefined
    if (!far) return at
    // ⚠ And the end NEARER the other thing, the dot or that point (Benoit, 2026-10-03: "...the line
    // should connect to the center of the close end of the node"). A node's own services are
    // beyond its name end, and a line to the far end ran over the whole badge.
    const to = { x: other.x ?? 0, y: other.y ?? 0 }
    return Math.hypot(far.x - to.x, far.y - to.y) < Math.hypot(at.x - to.x, at.y - to.y) ? far : at
  }

  private tick() {
    // Both ends of each link, worked out once: four attributes read them, on lines and on bridges.
    const ends = new Map(
      this.links.map(link => [
        link,
        [this.endOf(link.source as GraphNode, link.target as GraphNode), this.endOf(link.target as GraphNode, link.source as GraphNode)],
      ])
    )
    const origin = { x: 0, y: 0 }
    const x1 = (d: GraphLink) => (ends.get(d)?.[0] ?? origin).x
    const y1 = (d: GraphLink) => (ends.get(d)?.[0] ?? origin).y
    const x2 = (d: GraphLink) => (ends.get(d)?.[1] ?? origin).x
    const y2 = (d: GraphLink) => (ends.get(d)?.[1] ?? origin).y
    this.renderer.linkContainer.selectAll<SVGLineElement, GraphLink>('.link').attr('x1', x1).attr('y1', y1).attr('x2', x2).attr('y2', y2)
    this.renderer.bridgeContainer.selectAll<SVGLineElement, GraphLink>('.bridge').attr('x1', x1).attr('y1', y1).attr('x2', x2).attr('y2', y2)

    // ⚠ With its group's scale (`groupScale`): the glyph, its name and its badge grow round its middle.
    const transform = (d: GraphNode) => {
      const size = this.scaleOf(d)
      return `translate(${d.x ?? 0},${d.y ?? 0})${size !== 1 ? ` scale(${size})` : ''}`
    }
    this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point').attr('transform', transform)
    this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').attr('transform', transform)
    this.chips.selectAll<SVGRectElement, GraphNode>('rect.chip').attr('transform', transform)
    // The holes in the lines' mask move with their badges.
    this.holes.selectAll<SVGRectElement, SVGRectElement>('rect').attr('transform', chip => chip.getAttribute('transform'))
  }
}

/**
 * A badge: one box round an icon and its name, a little past each (Benoit, 2026-10-03, option C
 * for a service, then every node and network). The icon is at one end.
 */
function badgeOf(glyph: Box, name: Box, down = 4): Box {
  // The name is on either side of the icon. The icon's end is 3 px past the icon, the name's 8 px
  // past the name, and `down` px above and below the name.
  const x = Math.min(glyph.x - 3, name.x - 8)
  const right = Math.max(glyph.x + glyph.width + 3, name.x + name.width + 8)
  const y = Math.min(glyph.y - 1, name.y - down)
  const bottom = Math.max(glyph.y + glyph.height + 1, name.y + name.height + down)
  return { x, y, width: right - x, height: bottom - y }
}

/**
 * A node's badge before it is drawn: its name at a generous 7.4 px a character (the 12 px name
 * measures 6 to 7), on the side `sideOf` gives it. Generous, so that measuring it can only make
 * its ring smaller.
 */
function estimateBadge(label: string, side: 'right' | 'left'): Box {
  const icon = BADGE_ICONS.node!
  const width = label.length * 7.4
  const name = side === 'right' ? { x: icon.x + icon.width + BADGE_GAP, y: -8, width, height: 16 } : { x: icon.x - BADGE_GAP - width, y: -8, width, height: 16 }
  return badgeOf(icon, name, 3)
}

/** A box `size` times as large, round the same middle point (0, 0). */
function grow(box: Box, size: number): Box {
  return size === 1 ? box : { x: box.x * size, y: box.y * size, width: box.width * size, height: box.height * size }
}

/** The space above and below a badge's name: less on a node's, which is a smaller pill. */
function badgePad(d: GraphNode): number {
  return d.type === 'node' ? 3 : 4
}


/** A small stable number from an id, for a starting angle that does not change between draws. */
function hash(id: string): number {
  let value = 0
  for (let i = 0; i < id.length; i++) value = (value * 31 + id.charCodeAt(i)) >>> 0
  return value
}
